import { useEffect, useState, useCallback, useRef } from "react";
import api, { money, apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Switch } from "@/components/ui/switch";
import { Send, Trophy, Copy, Phone, History, Package, Link2, FileText, Download, MessageCircle, PackageCheck, Star, MapPin, FileDown, Receipt, Truck, ClipboardList, IndianRupee, Plus, Trash2, ShieldCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { InclusionsField } from "@/components/InclusionsField";
import { PlacesAutocomplete } from "@/components/PlacesAutocomplete";

const STATUS_OPTS = ["pending", "not_confirmed", "quote_required", "confirmed", "not_picked", "followup"];
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const EWAY_RE = /^[0-9]{12}$/;
const gstErr = (v) => (v && !GSTIN_RE.test(String(v).toUpperCase()) ? "Invalid GSTIN — 15 chars e.g. 27ABCDE1234F1Z5" : "");
const ewayErr = (v) => (v && !EWAY_RE.test(String(v)) ? "E-Way Bill must be exactly 12 digits" : "");

export default function LeadDrawer({ leadId, onClose, onChange, focusStep = null }) {
  const { user } = useAuth();
  const [lead, setLead] = useState(null);

  const load = useCallback(() => {
    if (!leadId) return;
    api.get(`/leads/${leadId}`).then((r) => setLead(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [leadId]);
  useEffect(() => { setLead(null); load(); }, [load]);

  useEffect(() => {
    if (lead && focusStep) {
      const el = document.getElementById("lead-stepflow");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
    }
  }, [lead, focusStep]);

  const refresh = () => { load(); onChange?.(); };
  const frozen = lead?.order_state === "confirmed_dispatched";

  const deleteLead = async () => {
    if (!window.confirm(`Delete lead ${lead?.order_number}? It will be hidden from the main list but preserved in History.`)) return;
    try { await api.delete(`/leads/${leadId}`); toast.success("Lead deleted — moved to History"); onClose(); onChange?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Sheet open={!!leadId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="lead-drawer">
        {!lead ? (
          <div className="p-8 text-muted-foreground">Loading…</div>
        ) : (
          <>
            <SheetHeader className="p-6 border-b border-border sticky top-0 bg-card z-10">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle className="font-mono-num text-primary text-xl">{lead.order_number}</SheetTitle>
                  <div className="text-sm text-muted-foreground capitalize">{lead.category} · {lead.pickup_city} → {lead.delivery_city}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={frozen ? "confirmed_dispatched" : lead.status} />
                  {(user?.role === "admin" || user?.role === "manager") && (
                    <Button variant="outline" size="icon" className="rounded-none h-8 w-8 text-destructive border-destructive/40 shrink-0" title="Delete lead (moves to History)" onClick={deleteLead} data-testid="delete-lead-btn"><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
            </SheetHeader>

            <div className="p-4 sm:p-6 space-y-6">
              <TripOverviewCard lead={lead} />
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`*Arrow Logistics* — Quote request for Order ${lead.order_number}${lead.lr_number ? ` (LR ${lead.lr_number})` : ""}.\nTrip: ${lead.pickup_city} → ${lead.delivery_city}.\nSubmit your best rate here: ${window.location.origin}/wq/${lead.share_token}`)}`}
                target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full h-11 bg-emerald-500 text-white font-bold uppercase tracking-wide hover:bg-emerald-600 transition-colors"
                data-testid="drawer-whatsapp-share"
              >
                <MessageCircle className="h-4 w-4" /> Share on WhatsApp for Vendor Bids
              </a>
              <OverviewPanel lead={lead} />

              <StepFlow lead={lead} refresh={refresh} frozen={frozen} focusStep={focusStep} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ---------------- Customer & Move overview (consolidated) ---------------- */
function TripOverviewCard({ lead }) {
  const lrs = lead.lrs || (lead.lr ? [lead.lr] : []);
  const lineTotal = (lead.quotation?.line_items || []).reduce((s, li) => s + (Number(li.amount) || 0), 0);
  const declared = (lead.items || []).reduce((s, it) => s + (Number(it.value) || 0), 0);
  const totalValue = lineTotal || declared;
  const tripStatus = () => {
    if (lrs.length) {
      const delivered = lrs.filter((l) => l.shipment_status === "delivered").length;
      if (delivered === lrs.length) return { label: "All Delivered", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/40" };
      if (delivered > 0) return { label: `${delivered}/${lrs.length} Delivered`, cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/40" };
      const moving = lrs.some((l) => ["in_transit", "on_the_way", "at_destination"].includes(l.shipment_status));
      return moving ? { label: "In Transit", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/40" } : { label: "Dispatched", cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/40" };
    }
    if (lead.order_state === "confirmed_dispatched") return { label: "Confirmed", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/40" };
    if (lead.quote_status === "sent") return { label: "Quote Sent", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/40" };
    if (lead.winning_vendor_id) return { label: "Quote Preparing", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/40" };
    return { label: "In Pipeline", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/40" };
  };
  const st = tripStatus();
  return (
    <div className="border-2 border-primary bg-primary/5 p-4" data-testid="trip-overview-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-bold uppercase tracking-widest text-primary">Trip Overview</span>
        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border ${st.cls}`} data-testid="trip-status">{st.label}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Value</div>
          <div className="font-mono-num font-bold text-lg" data-testid="trip-total-value">₹{Number(totalValue).toLocaleString("en-IN")}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Trucks</div>
          <div className="font-mono-num font-bold text-lg flex items-center gap-1.5" data-testid="trip-truck-count"><Truck className="h-4 w-4 text-primary" /> {lrs.length || "—"}</div>
        </div>
      </div>
    </div>
  );
}

const STEP_DEFS = [
  { key: "quotation", label: "Quotation", icon: FileText, done: (l) => l.quote_status && l.quote_status !== "none" },
  { key: "lr", label: "Lorry Receipt", icon: Truck, done: (l) => !!l.lr_number },
  { key: "vendor_docs", label: "Vendor Documents", icon: ShieldCheck, done: (l) => l.vendor_docs_status === "verified" },
  { key: "documents", label: "Generate Documents", icon: Receipt, done: (l) => !!l.lr_number },
  { key: "ops", label: "Operation Details", icon: MapPin, done: (l) => !!(l.driver_mobile || "").trim() },
  { key: "expenses", label: "Expenses", icon: IndianRupee, done: () => false },
  { key: "delivery", label: "Delivery & Feedback", icon: Star, done: (l) => l.status === "done" || !!l.delivered_at },
];

function StepFlow({ lead, refresh, frozen, focusStep }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!focusStep) return;
    const i = STEP_DEFS.findIndex((s) => s.key === focusStep);
    if (i >= 0) setStep(i);
  }, [focusStep]);
  const pct = Math.round(((step + 1) / STEP_DEFS.length) * 100);
  const cur = STEP_DEFS[step];

  return (
    <div className="space-y-5" id="lead-stepflow" data-testid="lead-stepflow">
      <div>
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
          <span>Step {step + 1} of {STEP_DEFS.length} · {cur.label}</span>
          <span className="font-mono-num">{pct}%</span>
        </div>
        <div className="h-1.5 bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all duration-300" style={{ width: `${pct}%` }} data-testid="lead-progress" />
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1" data-testid="lead-step-tabs">
        {STEP_DEFS.map((s, i) => {
          const done = s.done(lead);
          const active = i === step;
          return (
            <button
              key={s.key}
              onClick={() => setStep(i)}
              data-testid={`step-tab-${s.key}`}
              className={`shrink-0 flex flex-col items-center gap-1 px-2.5 py-2 border text-[10px] font-bold uppercase tracking-wide min-w-[66px] transition-colors ${active ? "border-primary bg-primary/10 text-primary" : done ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              <span className="relative">
                <s.icon className="h-4 w-4" />
                {done && !active && <span className="absolute -top-1.5 -right-1.5 h-2 w-2 rounded-full bg-emerald-500" />}
              </span>
              <span className="leading-tight text-center">{s.label}</span>
            </button>
          );
        })}
      </div>

      <div className="space-y-6" data-testid={`step-panel-${cur.key}`}>
        {cur.key === "quotation" && (
          <>
            <CallPanel lead={lead} refresh={refresh} />
            <BookingTypePanel lead={lead} refresh={refresh} frozen={frozen} />
            {lead.booking_type !== "self" && <RFQPanel lead={lead} refresh={refresh} frozen={frozen} />}
            {lead.booking_type !== "self" && <BiddingPanel lead={lead} refresh={refresh} frozen={frozen} />}
            <WhatsAppQuotesPanel lead={lead} refresh={refresh} />
            <QuotePanel lead={lead} refresh={refresh} frozen={frozen} />
          </>
        )}
        {cur.key === "lr" && <LorryReceiptPanel lead={lead} refresh={refresh} />}
        {cur.key === "vendor_docs" && <VendorDocsPanel lead={lead} refresh={refresh} />}
        {cur.key === "documents" && <DocumentsPanel lead={lead} />}
        {cur.key === "ops" && <OpsPanel lead={lead} refresh={refresh} frozen={frozen} />}
        {cur.key === "expenses" && <ExpensesPanel lead={lead} refresh={refresh} />}
        {cur.key === "delivery" && (
          <>
            <DeliveryPanel lead={lead} refresh={refresh} frozen={frozen} />
            <AuditPanel audit={lead.audit} />
          </>
        )}
      </div>

      <div className="flex justify-between pt-2 border-t border-border">
        <Button variant="outline" className="rounded-none gap-1" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="step-prev">Back</Button>
        <Button className="rounded-none gap-1 font-bold uppercase" disabled={step === STEP_DEFS.length - 1} onClick={() => setStep((s) => Math.min(STEP_DEFS.length - 1, s + 1))} data-testid="step-next">Next</Button>
      </div>
    </div>
  );
}

function OverviewPanel({ lead }) {
  const [showAll, setShowAll] = useState(false);
  const s = lead.logistics || {};
  const items = lead.items || [];
  const shown = showAll ? items : items.slice(0, 3);
  const valTotal = items.reduce((a, i) => a + (Number(i.value) || 0), 0);
  const YN = ({ k, label }) => (
    <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{label}</span><span className={`font-bold uppercase text-xs ${s[k] ? "text-emerald-500" : "text-muted-foreground"}`}>{s[k] ? "Yes" : "No"}</span></div>
  );
  return (
    <section className="border border-border p-4 space-y-4" data-testid="overview-panel">
      <SectionTitle icon={Package}>Customer &amp; Move Details</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
        <Info label="Customer" value={lead.customer_name} />
        <Info label="Phone" value={<span className="font-mono-num">{lead.customer_phone}</span>} />
        {lead.customer_company && <Info label="Company" value={lead.customer_company} />}
        {lead.customer_designation && <Info label="Designation" value={lead.customer_designation} />}
        <Info label="Pickup" value={`${lead.pickup_address}, ${lead.pickup_city}, ${lead.pickup_state}`} />
        <Info label="Delivery" value={`${lead.delivery_address}, ${lead.delivery_city}, ${lead.delivery_state}`} />
        <Info label="Origin floor / lift" value={`${lead.origin_floor || "—"} · ${lead.origin_lift ? "Lift Available" : "Lift Not Available"}`} />
        <Info label="Destination floor / lift" value={`${lead.dest_floor || "—"} · ${lead.dest_lift ? "Lift Available" : "Lift Not Available"}`} />
        {lead.packing_date && <Info label="Packing date" value={lead.packing_date} />}
        {lead.moving_date && <Info label="Moving date" value={lead.moving_date} />}
        {lead.delivery_date && <Info label="Delivery date" value={lead.delivery_date} />}
        {valTotal > 0 && <Info label="Declared value" value={money(valTotal)} />}
        {lead.order_inclusions && <div className="sm:col-span-2"><Info label="Inclusions (winning bid)" value={lead.order_inclusions} /></div>}
        {lead.notes && <div className="sm:col-span-2"><Info label="Remarks" value={lead.notes} /></div>}
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 border-t border-border pt-3">
        <YN k="storage" label="Storage needed" />
        <YN k="balcony_access" label="Balcony access" />
        <YN k="easy_loading" label="Easy loading / unloading" />
        <YN k="time_restrictions" label="Time restrictions" />
        <YN k="difficult_items" label="Difficult items" />
        <YN k="multi_location" label="Multi-location pickup/delivery" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5">Articles ({items.length})</div>
        <div className="border border-border divide-y divide-border">
          {shown.map((it, i) => <div key={i} className="flex justify-between p-2 px-3 text-sm" data-testid={`overview-item-${i}`}><span>{it.name}</span><span className="font-mono-num text-muted-foreground">×{it.quantity}{it.value ? ` · ₹${Number(it.value).toLocaleString("en-IN")}` : ""}</span></div>)}
          {!items.length && <div className="p-3 text-sm text-muted-foreground">No items</div>}
        </div>
        {items.length > 3 && (
          <Button variant="outline" size="sm" className="rounded-none mt-2 text-xs gap-1 border-2 border-primary text-primary font-bold uppercase tracking-wide hover:bg-primary hover:text-primary-foreground" onClick={() => setShowAll((v) => !v)} data-testid="view-all-items-btn">
            {showAll ? "Show less" : `View All Items (${items.length})`}
          </Button>
        )}
      </div>
    </section>
  );
}

/* ---------------- Move Survey (read-only) ---------------- */
function SurveyPanel({ lead }) {
  const s = lead.logistics || {};
  const valTotal = (lead.items || []).reduce((a, i) => a + (Number(i.value) || 0), 0);
  const hasSurvey = lead.customer_company || lead.customer_designation || lead.origin_floor || lead.dest_floor || lead.packing_date || lead.moving_date || lead.delivery_date || Object.values(s).some(Boolean) || valTotal > 0;
  if (!hasSurvey) return null;
  const YN = ({ k, label }) => (
    <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{label}</span><span className={`font-bold uppercase text-xs ${s[k] ? "text-emerald-500" : "text-muted-foreground"}`}>{s[k] ? "Yes" : "No"}</span></div>
  );
  return (
    <section className="border border-border p-4" data-testid="survey-panel">
      <SectionTitle icon={FileText}>Move Survey</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
        {lead.customer_company && <Info label="Company" value={lead.customer_company} />}
        {lead.customer_designation && <Info label="Designation" value={lead.customer_designation} />}
        <Info label="Origin floor / lift" value={`${lead.origin_floor || "—"} · Lift: ${lead.origin_lift ? "Yes" : "No"}`} />
        <Info label="Destination floor / lift" value={`${lead.dest_floor || "—"} · Lift: ${lead.dest_lift ? "Yes" : "No"}`} />
        {lead.packing_date && <Info label="Packing date" value={lead.packing_date} />}
        {lead.moving_date && <Info label="Moving date" value={lead.moving_date} />}
        {lead.delivery_date && <Info label="Delivery date" value={lead.delivery_date} />}
        {valTotal > 0 && <Info label="Declared value" value={money(valTotal)} />}
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 border-t border-border pt-3">
        <YN k="storage" label="Storage needed" />
        <YN k="balcony_access" label="Balcony access" />
        <YN k="easy_loading" label="Easy loading / unloading" />
        <YN k="time_restrictions" label="Time restrictions" />
        <YN k="difficult_items" label="Difficult items" />
        <YN k="multi_location" label="Multi-location pickup/delivery" />
      </div>
    </section>
  );
}

/* ---------------- Call logging ---------------- */
function CallPanel({ lead, refresh }) {
  const [status, setStatus] = useState(lead.status);
  const [followup, setFollowup] = useState(lead.followup_datetime ? lead.followup_datetime.slice(0, 16) : "");
  const [remarks, setRemarks] = useState(lead.remarks || "");
  const [movingDate, setMovingDate] = useState(lead.moving_date || lead.move_date || "");
  useEffect(() => { setStatus(lead.status); setFollowup(lead.followup_datetime ? lead.followup_datetime.slice(0, 16) : ""); setRemarks(lead.remarks || ""); setMovingDate(lead.moving_date || lead.move_date || ""); }, [lead]);

  const save = async () => {
    if (!movingDate) return toast.error("Moving Date is required");
    try {
      await api.patch(`/leads/${lead.id}/status`, { status, followup_datetime: followup ? new Date(followup).toISOString() : null, remarks, moving_date: movingDate });
      toast.success("Call logged"); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <section className="border border-border p-4 bg-muted/40">
      <SectionTitle icon={Phone}>Call Update</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none bg-card" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>Follow-up date &amp; time</Label>
          <Input type="datetime-local" className="rounded-none bg-card" value={followup} onChange={(e) => setFollowup(e.target.value)} data-testid="followup-datetime" />
        </div>
      </div>
      <div className="space-y-2 mt-4"><Label>Moving Date <span className="text-red-500">*</span></Label>
        <Input type="date" className="rounded-none bg-card" value={movingDate} onChange={(e) => setMovingDate(e.target.value)} data-testid="call-moving-date" />
      </div>
      <div className="space-y-2 mt-4"><Label>Remarks</Label>
        <Textarea className="rounded-none bg-card" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Notes from the call…" data-testid="lead-remarks" />
      </div>
      <Button className="mt-3 rounded-none font-bold uppercase tracking-wide" onClick={save} data-testid="save-call-log">Save update</Button>
    </section>
  );
}

/* ---------------- Booking Type (Self / Vendor) ---------------- */
const TRANSPORT_TYPES = ["HHG", "Car", "Office", "DTD", "Custom"];
const TRANSPORT_PRESETS = ["HHG", "Car", "Office", "DTD"];
function BookingTypePanel({ lead, refresh, frozen }) {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState(lead.self_vehicle_id || "");
  const [cost, setCost] = useState("");
  const [busy, setBusy] = useState(false);
  const [consCount, setConsCount] = useState(lead.consignment_count || 1);
  const [consTypes, setConsTypes] = useState(() => (lead.consignments || []).map((c) => c.transport_type || ""));
  const type = lead.booking_type === "self" ? "self" : "vendor";

  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  useEffect(() => { setConsTypes((lead.consignments || []).map((c) => c.transport_type || "")); setConsCount(lead.consignment_count || 1); /* eslint-disable-next-line */ }, [lead.id]);

  const setVendor = async () => {
    setBusy(true);
    try { await api.post(`/leads/${lead.id}/booking-type`, { booking_type: "vendor" }); toast.success("Switched to Vendor booking"); refresh(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const setSelf = async () => {
    if (!vehicleId) return toast.error("Select a vehicle first");
    setBusy(true);
    try { await api.post(`/leads/${lead.id}/booking-type`, { booking_type: "self", vehicle_id: vehicleId, cost: parseFloat(cost) || 0 }); toast.success("Self booking assigned — bidding bypassed"); refresh(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const setConsignments = async (n) => {
    setConsCount(n);
    try {
      await api.post(`/leads/${lead.id}/consignments`, { count: n, descriptions: (lead.consignments || []).map((c) => c.description || ""), transport_types: consTypes });
      toast.success(n === 1 ? "Single consignment (standard flow)" : `${n} consignments — one RFQ tab per truck`);
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveConsTypes = async (types) => {
    setConsTypes(types);
    try {
      await api.post(`/leads/${lead.id}/consignments`, { count: consCount, descriptions: (lead.consignments || []).map((c) => c.description || ""), transport_types: types });
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <section className="border border-border p-4" data-testid="booking-type-panel">
      <SectionTitle icon={Truck}>Booking Type</SectionTitle>
      <div className="text-sm mb-3">
        Current: <b className={`uppercase ${type === "self" ? "text-emerald-500" : "text-primary"}`}>{type}</b> booking
        {type === "self" && lead.self_vehicle_label ? <span className="text-muted-foreground"> · {lead.self_vehicle_label}</span> : null}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className={`border p-3 space-y-2 ${type === "self" ? "border-emerald-500/50 bg-emerald-500/5" : "border-border"}`}>
          <div className="text-xs font-bold uppercase tracking-wide">Self · Own Vehicle</div>
          <Select value={vehicleId} onValueChange={setVehicleId} disabled={frozen}>
            <SelectTrigger className="rounded-none bg-card h-9" data-testid="booking-vehicle-select"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
            <SelectContent>
              {vehicles.length === 0 && <div className="p-2 text-xs text-muted-foreground">No vehicles — add in Vehicle Management</div>}
              {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate_number}{v.model ? ` · ${v.model}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" className="rounded-none bg-card h-9 font-mono-num" placeholder="Internal cost (optional)" value={cost} onChange={(e) => setCost(e.target.value)} disabled={frozen} data-testid="booking-self-cost" />
          <Button className="rounded-none w-full uppercase text-xs font-bold" onClick={setSelf} disabled={frozen || busy} data-testid="booking-assign-self">{type === "self" ? "Update Self Booking" : "Assign Self · Skip Bidding"}</Button>
        </div>
        <div className={`border p-3 space-y-2 ${type === "vendor" ? "border-primary/50 bg-primary/5" : "border-border"}`}>
          <div className="text-xs font-bold uppercase tracking-wide">Vendor · Bidding</div>
          <p className="text-xs text-muted-foreground">Send RFQs &amp; collect competitive bids from transport partners below.</p>
          <Button variant="outline" className="rounded-none w-full uppercase text-xs font-bold" onClick={setVendor} disabled={frozen || busy || type === "vendor"} data-testid="booking-set-vendor">{type === "vendor" ? "Vendor Selected ✓" : "Switch to Vendor"}</Button>
        </div>
      </div>
      {type === "vendor" && (
        <div className="mt-3 border-t border-border pt-3">
          <Label className="text-xs font-bold uppercase tracking-wide">Number of LRs / Consignments</Label>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Select value={String(consCount)} onValueChange={(v) => setConsignments(Number(v))} disabled={frozen}>
              <SelectTrigger className="rounded-none bg-card h-9 w-28" data-testid="consignment-count"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "LR" : "LRs"}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground flex-1 min-w-[200px]">Pick 2+ to send a separate RFQ per truck. RFQ 1 = full order details; RFQ 2+ let you add a custom goods description.</span>
          </div>
          {consCount >= 2 && (
            <div className="mt-3 space-y-2" data-testid="cons-transport-types">
              <Label className="text-xs font-bold uppercase tracking-wide">Transportation Type · Per Consignment</Label>
              <div className="grid sm:grid-cols-2 gap-3">
                {Array.from({ length: consCount }).map((_, i) => {
                  const cur = consTypes[i] || "";
                  const isCustom = !!cur && !TRANSPORT_PRESETS.includes(cur);
                  const setType = (val) => { const next = [...consTypes]; while (next.length < consCount) next.push(""); next[i] = val; saveConsTypes(next); };
                  const setCustomText = (val) => { const next = [...consTypes]; while (next.length < consCount) next.push(""); next[i] = val; setConsTypes(next); };
                  return (
                    <div key={i} className="border border-border p-2.5 space-y-1.5">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Consignment {i + 1}</div>
                      <Select value={isCustom ? "Custom" : cur} onValueChange={setType} disabled={frozen}>
                        <SelectTrigger className="rounded-none bg-card h-9" data-testid={`cons-transport-type-${i}`}><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>{TRANSPORT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                      {isCustom && <Input className="rounded-none bg-card h-9 text-xs" value={cur === "Custom" ? "" : cur} onChange={(e) => setCustomText(e.target.value)} onBlur={() => saveConsTypes(consTypes)} disabled={frozen} placeholder="Enter custom type…" data-testid={`cons-transport-custom-${i}`} />}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground">These types feed each consignment's RFQ &amp; the quotation.</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* ---------------- RFQ ---------------- */
function RFQPanel({ lead, refresh, frozen }) {
  const [vendors, setVendors] = useState([]);
  const [locs, setLocs] = useState({ states: [], cities: [] });
  const [fState, setFState] = useState("all");
  const [fCity, setFCity] = useState("all");
  const [picked, setPicked] = useState([]);
  const [svc, setSvc] = useState({ loading: lead.service_lines?.loading || false, unloading: lead.service_lines?.unloading || false, packaging: lead.service_lines?.packaging || false });
  const count = lead.consignment_count || 1;
  const [activeCons, setActiveCons] = useState(0);
  const [desc, setDesc] = useState("");
  useEffect(() => { const c = (lead.consignments || [])[activeCons]; setDesc(c?.description || ""); setPicked([]); /* eslint-disable-next-line */ }, [activeCons, lead]);
  const saveDesc = async () => {
    const descs = (lead.consignments || []).map((c) => c.description || "");
    while (descs.length < count) descs.push("");
    descs[activeCons] = desc;
    try { await api.post(`/leads/${lead.id}/consignments`, { count, descriptions: descs }); toast.success("Consignment description saved"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const loadVendors = useCallback(() => {
    const p = new URLSearchParams();
    if (fState !== "all") p.set("state", fState);
    if (fCity !== "all") p.set("city", fCity);
    api.get(`/vendors?${p}`).then((r) => setVendors(r.data));
  }, [fState, fCity]);
  useEffect(() => { api.get("/vendors/locations").then((r) => setLocs(r.data)); }, []);
  useEffect(() => { loadVendors(); }, [loadVendors]);

  const toggle = (id) => setPicked((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const waVendorLink = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");
    const num = digits.length === 10 ? "91" + digits : digits;
    const url = `${window.location.origin}/wq/${lead.share_token}`;
    const c = (lead.consignments || [])[activeCons];
    const msg = activeCons > 0
      ? `*Arrow Logistics* — Rate request for ${c?.description || `Consignment ${activeCons + 1}`} (Order ${lead.order_number}).\nPlease submit your best rate here: ${url}`
      : `*Arrow Logistics* — RFQ for Order ${lead.order_number}${lead.lr_number ? ` (LR ${lead.lr_number})` : ""}.\nTrip: ${lead.pickup_city} → ${lead.delivery_city}.\nPlease submit your best rate here: ${url}`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  const send = async () => {
    if (!picked.length) return toast.error("Select at least one partner");
    try { await api.post(`/leads/${lead.id}/rfq`, { vendor_ids: picked, ...svc, consignment_idx: activeCons }); toast.success(`RFQ (Consignment ${activeCons + 1}) sent to ${picked.length} partner(s)`); setPicked([]); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const sendAll = async () => {
    const ids = vendors.map((v) => v.id);
    if (!ids.length) return toast.error("No partners match the current filter");
    if (!window.confirm(`Send the RFQ for Consignment ${activeCons + 1} to all ${ids.length} partner(s) in one go?`)) return;
    try { await api.post(`/leads/${lead.id}/rfq`, { vendor_ids: ids, ...svc, consignment_idx: activeCons }); toast.success(`RFQ sent to all ${ids.length} partner(s)`); setPicked([]); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const invitedForCons = (lead.bids || []).filter((b) => (b.consignment_idx || 0) === activeCons && b.bid_token && b.phone);
  const blastLinks = () => {
    if (!invitedForCons.length) return toast.error("No invited vendors for this RFQ yet — send the RFQ first");
    const c = (lead.consignments || [])[activeCons];
    const label = activeCons === 0 ? "the full order" : (c?.description || `Consignment ${activeCons + 1}`);
    invitedForCons.forEach((b) => {
      const digits = String(b.phone || "").replace(/\D/g, "");
      const num = digits.length === 10 ? "91" + digits : digits;
      const link = `${window.location.origin}/bid/${b.bid_token}`;
      const trip = activeCons === 0 ? `\n${lead.pickup_city} → ${lead.delivery_city}, move ${lead.move_date || "TBD"}.` : "";
      const msg = `*Arrow Logistics* — Rate request for ${label} (Order ${lead.order_number}).${trip}\nPlease submit your quote here: ${link}`;
      if (num) window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
    });
    toast.success(`Opened WhatsApp for ${invitedForCons.length} vendor(s)`);
  };

  return (
    <section className="border border-border p-4">
      <SectionTitle icon={Send}>Send RFQ (Anonymized)</SectionTitle>
      {(lead.rfqs || []).length > 0 && (
        <div className="mb-3 border border-border bg-muted/30" data-testid="rfq-history">
          <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground border-b border-border">RFQ History ({lead.rfqs.length})</div>
          <div className="max-h-40 overflow-y-auto divide-y divide-border">
            {[...lead.rfqs].reverse().map((r, i) => (
              <div key={r.id || i} className="px-3 py-2 text-xs" data-testid={`rfq-history-item-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold">RFQ {(r.consignment_idx || 0) + 1} · {r.count} vendor(s)</span>
                  <span className="text-muted-foreground">{r.sent_at ? new Date(r.sent_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                </div>
                <div className="text-muted-foreground truncate" title={(r.vendor_names || []).join(", ")}>{(r.vendor_names || []).join(", ") || "—"}</div>
                {r.sent_by_name && <div className="text-[10px] text-muted-foreground">Sent by {r.sent_by_name}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {count > 1 && (
        <div className="flex gap-1 overflow-x-auto border-b border-border mb-3" data-testid="rfq-tabs">
          {Array.from({ length: count }).map((_, i) => (
            <button key={i} onClick={() => setActiveCons(i)} data-testid={`rfq-tab-${i + 1}`}
              className={`shrink-0 px-3 py-2 text-xs font-bold uppercase tracking-wide border-b-2 -mb-px transition-colors ${i === activeCons ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              RFQ {i + 1}
            </button>
          ))}
        </div>
      )}
      {count > 1 && activeCons > 0 && (
        <div className="mb-3 border border-border bg-muted/40 p-3 space-y-2" data-testid="rfq-desc-block">
          <Label className="text-xs font-bold uppercase tracking-wide">Consignment {activeCons + 1} — Goods description</Label>
          <Textarea rows={2} className="rounded-none bg-card text-sm" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. 1 car (Hatchback) + 5 boxes fragile" data-testid="rfq-desc-input" />
          <Button size="sm" variant="outline" className="rounded-none text-xs" onClick={saveDesc} data-testid="rfq-desc-save">Save description</Button>
        </div>
      )}
      {count > 1 && activeCons === 0 && <p className="text-[11px] text-muted-foreground mb-2">RFQ 1 carries the complete order details (items, route, services) shown below.</p>}
      {invitedForCons.length > 0 && (
        <Button variant="outline" className="rounded-none gap-2 mb-3 text-xs font-bold uppercase border-emerald-500/50 text-emerald-600 dark:text-emerald-400" onClick={blastLinks} disabled={frozen} data-testid="blast-rfq-links">
          <MessageCircle className="h-4 w-4" /> WhatsApp Bid Link to All Invited ({invitedForCons.length})
        </Button>
      )}
      <p className="text-xs text-muted-foreground mb-3">Customer name & contact are stripped before partners receive the request.</p>
      <div className="flex flex-wrap gap-2 mb-3 text-sm">
        {[["loading", "Loading"], ["unloading", "Unloading"], ["packaging", "Packaging"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 border border-border px-3 py-1.5 cursor-pointer bg-card">
            <Checkbox checked={svc[k]} onCheckedChange={(c) => setSvc({ ...svc, [k]: !!c })} disabled={frozen} data-testid={`svc-${k}`} /> {l} Required
          </label>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        <Select value={fState} onValueChange={(v) => { setFState(v); }}>
          <SelectTrigger className="rounded-none w-40" data-testid="rfq-filter-state"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All states</SelectItem>{locs.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fCity} onValueChange={(v) => setFCity(v)}>
          <SelectTrigger className="rounded-none w-40" data-testid="rfq-filter-city"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All cities</SelectItem>{locs.cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="border border-border max-h-48 overflow-y-auto divide-y divide-border">
        {vendors.length === 0 && <div className="p-3 text-sm text-muted-foreground">No partners match filter.</div>}
        {vendors.map((v) => (
          <div key={v.id} className="flex items-center gap-3 p-2 px-3 hover:bg-muted text-sm" data-testid={`rfq-vendor-${v.id}`}>
            <label className="flex items-center gap-3 flex-1 cursor-pointer">
              <Checkbox checked={picked.includes(v.id)} onCheckedChange={() => toggle(v.id)} disabled={frozen} />
              <span className="flex-1"><span className="font-medium">{v.name}</span> <span className="text-muted-foreground text-xs">· {v.city}, {v.state}</span></span>
              <span className="font-mono-num text-xs text-muted-foreground">{v.phone}</span>
            </label>
            <a
              href={waVendorLink(v.phone)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={`Send RFQ link to ${v.name} on WhatsApp`}
              className="inline-flex items-center justify-center h-7 w-7 border border-emerald-500/40 text-emerald-500 hover:bg-emerald-500 hover:text-white transition-colors shrink-0"
              data-testid={`rfq-whatsapp-${v.id}`}
            >
              <MessageCircle className="h-4 w-4" />
            </a>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={send} disabled={frozen} data-testid="send-rfq-btn"><Send className="h-4 w-4" /> Send RFQ ({picked.length})</Button>
        <Button variant="outline" className="rounded-none gap-2 font-bold uppercase tracking-wide border-primary text-primary" onClick={sendAll} disabled={frozen || !vendors.length} data-testid="send-all-rfq-btn"><Send className="h-4 w-4" /> Send All RFQs ({vendors.length})</Button>
      </div>
    </section>
  );
}

/* ---------------- Bidding ---------------- */
function BiddingPanel({ lead, refresh, frozen }) {
  const [prices, setPrices] = useState({});
  const [showManual, setShowManual] = useState(false);
  const [manualCons, setManualCons] = useState(0);
  const [mq, setMq] = useState({ vendor_name: "", phone: "", location: "", quoted_price: "", inclusions: "" });
  const key = (b) => `${b.vendor_id}:${b.consignment_idx || 0}`;
  useEffect(() => { const p = {}; (lead.bids || []).forEach((b) => { p[key(b)] = b.quoted_price ?? ""; }); setPrices(p); }, [lead]);
  const bids = lead.bids || [];
  const count = lead.consignment_count || 1;
  const cons = lead.consignments || [];
  const winnerFor = (ci) => cons[ci]?.winning_vendor_id;

  const saveBid = async (b) => {
    const val = parseFloat(prices[key(b)]);
    if (isNaN(val)) return toast.error("Enter a valid price");
    try { await api.patch(`/leads/${lead.id}/bid/${b.vendor_id}?consignment_idx=${b.consignment_idx || 0}`, { quoted_price: val }); toast.success("Bid recorded"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const selectWinner = async (b) => {
    try {
      await api.post(`/leads/${lead.id}/select-winner/${b.vendor_id}?consignment_idx=${b.consignment_idx || 0}`);
      toast.success(`Winner set for Consignment ${(b.consignment_idx || 0) + 1}`);
      const digits = String(b.phone || "").replace(/\D/g, "");
      const num = digits.length === 10 ? "91" + digits : digits;
      const msg = `*Arrow Logistics* — LOAD CONFIRMED ✅\nOrder ${lead.order_number}\nTrip: ${lead.pickup_city} → ${lead.delivery_city}\nMove date: ${lead.move_date || "TBD"}\nAgreed rate: ₹${Number(b.quoted_price ?? 0).toLocaleString("en-IN")}\nPlease proceed with the pickup.`;
      if (num) window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const selectBid = async (b) => {
    try {
      await api.post(`/leads/${lead.id}/select-bid/${b.vendor_id}?consignment_idx=${b.consignment_idx || 0}`);
      toast.success(`${b.vendor_name}'s bid copied into the Quotation as the draft vendor quote`);
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const addManual = async () => {
    const val = parseFloat(mq.quoted_price);
    if (!mq.vendor_name.trim()) return toast.error("Enter the vendor / partner name");
    if (isNaN(val) || val <= 0) return toast.error("Enter a valid quoted price");
    try {
      await api.post(`/leads/${lead.id}/manual-bid`, { vendor_name: mq.vendor_name.trim(), phone: mq.phone.trim(), location: mq.location.trim(), quoted_price: val, inclusions: mq.inclusions.trim(), consignment_idx: manualCons });
      toast.success("Manual quote added");
      setMq({ vendor_name: "", phone: "", location: "", quoted_price: "", inclusions: "" });
      setShowManual(false);
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const minFor = (ci) => {
    const vals = bids.filter((b) => (b.consignment_idx || 0) === ci && b.quoted_price != null).map((b) => Number(b.quoted_price));
    return vals.length ? Math.min(...vals) : null;
  };

  const consLabel = (ci) => ci === 0 ? (count > 1 ? "Consignment 1 · Full Order" : "Order") : ((cons[ci]?.description || `Consignment ${ci + 1}`));

  const renderTable = (ci) => {
    const rows = bids.filter((b) => (b.consignment_idx || 0) === ci);
    const minPrice = minFor(ci);
    if (rows.length === 0) return <div className="p-4 text-sm text-muted-foreground border border-border" data-testid={`no-bids-${ci}`}>No bids yet for this consignment. Send an RFQ or add a manual quote.</div>;
    return (
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm" data-testid={`bidding-table-${ci}`}>
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2">Partner</th><th className="px-3">Location</th><th className="px-3">Inclusions</th><th className="px-3">Quoted ₹</th><th className="px-3"></th>
          </tr></thead>
          <tbody>
            {rows.map((b) => {
              const win = winnerFor(ci) === b.vendor_id;
              const selected = ci === 0 && lead.selected_vendor_id === b.vendor_id;
              const isLowest = b.quoted_price != null && Number(b.quoted_price) === minPrice;
              return (
                <tr key={key(b)} className={`border-b border-border last:border-0 ${win ? "bg-primary/10" : isLowest ? "bg-emerald-500/10" : ""}`} data-testid={`bid-row-${b.vendor_id}-${ci}`}>
                  <td className="px-3 py-2 font-medium">{b.vendor_name}{win && <span className="ml-2 text-[10px] font-bold uppercase text-primary">Winner</span>}{!win && b.status === "rejected" && <span className="ml-2 text-[10px] font-bold uppercase text-destructive">Rejected</span>}{!win && b.status !== "rejected" && isLowest && <span className="ml-2 text-[10px] font-bold uppercase text-emerald-500">Potential Winner</span>}</td>
                  <td className="px-3 text-xs text-muted-foreground">{b.location}</td>
                  <td className="px-3 text-xs text-muted-foreground max-w-[140px]">{b.inclusions || "—"}</td>
                  <td className="px-3">
                    <div className="flex items-center gap-2">
                      <span className={`font-mono-num font-bold w-20 ${isLowest ? "text-emerald-500" : ""}`} data-testid={`bid-price-${b.vendor_id}-${ci}`}>{b.quoted_price != null ? money(b.quoted_price) : "—"}</span>
                      <Input className="rounded-none h-8 w-24 font-mono-num" value={prices[key(b)] ?? ""} onChange={(e) => setPrices({ ...prices, [key(b)]: e.target.value })} disabled={frozen} data-testid={`bid-input-${b.vendor_id}-${ci}`} />
                      <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => saveBid(b)} disabled={frozen} data-testid={`save-bid-${b.vendor_id}-${ci}`}>Save</Button>
                      {b.bid_token && <Button size="sm" variant="ghost" className="rounded-none h-8 px-2" title="Copy partner bid link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/bid/${b.bid_token}`); toast.success("Partner bid link copied"); }} data-testid={`copy-bid-link-${b.vendor_id}`}><Link2 className="h-4 w-4" /></Button>}
                    </div>
                  </td>
                  <td className="px-3">
                    {ci === 0 ? (
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant={selected ? "secondary" : "outline"} className="rounded-none h-8 gap-1 font-bold uppercase text-xs whitespace-nowrap" onClick={() => selectBid(b)} disabled={frozen || b.quoted_price == null} title="Copy this bid into the Quotation for drafting" data-testid={`select-bid-${b.vendor_id}-${ci}`}>{selected ? "Selected ✓" : "Select"}</Button>
                        <Button size="sm" variant={win ? "secondary" : "default"} className="rounded-none h-8 gap-1 font-bold uppercase text-xs whitespace-nowrap" onClick={() => selectWinner(b)} disabled={frozen || b.quoted_price == null || win} title="Finalize this vendor & send WhatsApp confirmation" data-testid={`select-winner-${b.vendor_id}-${ci}`}><Trophy className="h-3 w-3" /> {win ? "Winner ✓" : "Winner"}</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant={win ? "secondary" : "default"} className="rounded-none h-8 gap-1 font-bold uppercase text-xs whitespace-nowrap" onClick={() => selectWinner(b)} disabled={frozen || b.quoted_price == null || win} data-testid={`select-winner-${b.vendor_id}-${ci}`}><Trophy className="h-3 w-3" /> {win ? "Winner ✓" : "Select"}</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <section className="border border-border p-4" data-testid="bidding-panel">
      <div className="flex items-center justify-between mb-2">
        <SectionTitle icon={Trophy}>Vendor Bidding</SectionTitle>
        <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs" onClick={() => setShowManual((s) => !s)} disabled={frozen} data-testid="add-manual-quote-btn"><Plus className="h-3.5 w-3.5" /> Add Manual Quote</Button>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3" data-testid="bidding-legend"><b>Select</b> copies a bid into the Quotation for drafting (no commitment). <b>Winner</b> finalizes the vendor &amp; opens a WhatsApp confirmation to them.</p>
      {showManual && (
        <div className="border border-primary/40 bg-primary/5 p-3 mb-3 space-y-3" data-testid="manual-quote-form">
          <div className="grid sm:grid-cols-6 gap-2 items-end">
            <div className="sm:col-span-2 space-y-1"><Label className="text-xs">Vendor / Partner name</Label><Input className="rounded-none bg-card h-9" value={mq.vendor_name} onChange={(e) => setMq({ ...mq, vendor_name: e.target.value })} placeholder="e.g. Sharma Transport" data-testid="manual-vendor-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Phone</Label><Input className="rounded-none bg-card h-9 font-mono-num" value={mq.phone} onChange={(e) => setMq({ ...mq, phone: e.target.value })} data-testid="manual-vendor-phone" /></div>
            <div className="space-y-1"><Label className="text-xs">Quoted ₹</Label><Input type="number" className="rounded-none bg-card h-9 font-mono-num" value={mq.quoted_price} onChange={(e) => setMq({ ...mq, quoted_price: e.target.value })} data-testid="manual-vendor-price" /></div>
            {count > 1 && <div className="space-y-1"><Label className="text-xs">Consignment</Label>
              <Select value={String(manualCons)} onValueChange={(v) => setManualCons(Number(v))}><SelectTrigger className="rounded-none bg-card h-9" data-testid="manual-consignment"><SelectValue /></SelectTrigger>
                <SelectContent>{Array.from({ length: count }).map((_, i) => <SelectItem key={i} value={String(i)}>RFQ {i + 1}</SelectItem>)}</SelectContent></Select></div>}
            <div className="flex gap-2"><Button className="rounded-none h-9 flex-1 font-bold uppercase text-xs" onClick={addManual} data-testid="save-manual-quote">Save</Button></div>
          </div>
          <InclusionsField value={mq.inclusions} onChange={(v) => setMq({ ...mq, inclusions: v })} testid="manual-inclusions" />
        </div>
      )}
      {bids.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground border border-border" data-testid="no-bids-empty">No bids yet. Send an RFQ to vendors, or use <b>Add Manual Quote</b> to record a phone quote.</div>
      ) : (
        <div className="space-y-4">
          {Array.from({ length: count }).map((_, ci) => (
            <div key={ci} data-testid={`bidding-consignment-${ci}`}>
              {count > 1 && <div className="text-xs font-bold uppercase tracking-widest text-primary mb-1.5">RFQ {ci + 1} · {consLabel(ci)}</div>}
              {renderTable(ci)}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------------- WhatsApp quotes comparison ---------------- */
function WhatsAppQuotesPanel({ lead, refresh }) {
  const quotes = lead.whatsapp_quotes || [];
  if (!quotes.length) return null;
  const minRate = Math.min(...quotes.map((q) => Number(q.rate)));
  const accept = async (id) => {
    try { await api.post(`/quotes/${id}/accept`); toast.success("WhatsApp quote accepted"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <section className="border border-border p-4">
      <SectionTitle icon={MessageCircle}>WhatsApp Quotes ({quotes.length})</SectionTitle>
      <p className="text-xs text-muted-foreground mb-2">Rates submitted by vendors via WhatsApp — sorted low to high. Lowest highlighted for comparison.</p>
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm" data-testid="wa-quotes-table">
          <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2">Transporter</th><th className="px-3 text-right">Rate ₹</th><th className="px-3">Inclusions</th><th className="px-3">Status</th><th className="px-3 text-right">Action</th>
          </tr></thead>
          <tbody>
            {quotes.map((q) => {
              const isLowest = Number(q.rate) === minRate;
              const accepted = q.status === "accepted";
              return (
                <tr key={q.id} className={`border-b border-border last:border-0 ${accepted ? "bg-primary/10" : isLowest ? "bg-emerald-500/10" : ""}`} data-testid={`wa-quote-${q.id}`}>
                  <td className="px-3 py-2 font-medium">{q.vendor_name}{accepted && <span className="ml-2 text-[10px] font-bold uppercase text-primary">Accepted</span>}{!accepted && isLowest && <span className="ml-2 text-[10px] font-bold uppercase text-emerald-500">Lowest</span>}</td>
                  <td className={`px-3 text-right font-mono-num font-bold ${isLowest ? "text-emerald-500" : ""}`}>{money(q.rate)}</td>
                  <td className="px-3 text-xs text-muted-foreground max-w-[180px]">{q.inclusions || "—"}</td>
                  <td className="px-3"><span className="text-[11px] uppercase font-bold">{q.status}</span></td>
                  <td className="px-3 text-right">
                    {q.status === "pending"
                      ? <Button size="sm" className="rounded-none h-8 gap-1 font-bold uppercase text-xs" onClick={() => accept(q.id)} data-testid={`wa-accept-${q.id}`}><Trophy className="h-3 w-3" /> Accept</Button>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ---------------- Operations Details ---------------- */
function OpsPanel({ lead, refresh, frozen }) {
  const [loadType, setLoadType] = useState(lead.load_type || "");
  const [assign, setAssign] = useState(lead.assign || "");
  const [carVendor, setCarVendor] = useState(lead.car_vendor || "");
  const [carAmount, setCarAmount] = useState(lead.car_amount ?? "");
  const [vendors, setVendors] = useState([]);
  useEffect(() => { api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try {
      await api.patch(`/leads/${lead.id}/ops`, {
        assign, load_type: loadType, car_vendor: carVendor,
        car_amount: carAmount === "" ? null : parseFloat(carAmount) || 0,
      });
      toast.success("Operations details saved");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <section className="border border-border p-4" data-testid="ops-panel">
      <SectionTitle icon={Truck}>Operations Details</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Load Type</Label>
          <Select value={loadType || "none"} onValueChange={(v) => setLoadType(v === "none" ? "" : v)} disabled={frozen}>
            <SelectTrigger className="rounded-none bg-card h-9" data-testid="ops-load-type"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              <SelectItem value="FTL">FTL — Full Truck Load</SelectItem>
              <SelectItem value="PTL">PTL — Part Truck Load</SelectItem>
              <SelectItem value="LCV">LCV — Light Commercial Vehicle</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Assignment (ASSIGN)</Label>
          <Input className="rounded-none bg-card h-9" value={assign} onChange={(e) => setAssign(e.target.value)} disabled={frozen} placeholder="e.g. marker / assignee note" data-testid="ops-assign" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Car Transport Vendor</Label>
          <Select value={carVendor || "none"} onValueChange={(v) => setCarVendor(v === "none" ? "" : v)} disabled={frozen}>
            <SelectTrigger className="rounded-none bg-card h-9" data-testid="ops-car-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="none">— None —</SelectItem>
              {vendors.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}{v.city ? ` · ${v.city}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Car Transport Amount ₹</Label>
          <Input type="number" className="rounded-none bg-card h-9 font-mono-num" value={carAmount} onChange={(e) => setCarAmount(e.target.value)} disabled={frozen} data-testid="ops-car-amount" /></div>
      </div>
      <Button className="rounded-none mt-3 font-bold uppercase tracking-wide" onClick={save} disabled={frozen} data-testid="ops-save">Save Ops Details</Button>
    </section>
  );
}

/* ---------------- Quotation (editable) ---------------- */
function QuotePanel({ lead, refresh, frozen }) {
  const hasWinner = !!lead.winning_vendor_id || !!lead.whatsapp_winner;
  const q = lead.quote || {};
  const trackLink = `${window.location.origin}/track/${lead.track_token}`;
  const [busy, setBusy] = useState(false);
  const [waBusy, setWaBusy] = useState(false);
  const [markupType, setMarkupType] = useState(lead.markup_type || "fixed");
  const [markupValue, setMarkupValue] = useState(lead.markup_value != null ? String(lead.markup_value) : "");
  const [gstEnabled, setGstEnabled] = useState((lead.quotation?.gst_enabled) !== false);
  const [route, setRoute] = useState({
    pickup_city: lead.pickup_city || "", pickup_state: lead.pickup_state || "", pickup_address: lead.pickup_address || "",
    delivery_city: lead.delivery_city || "", delivery_state: lead.delivery_state || "", delivery_address: lead.delivery_address || "",
  });
  const [savingRoute, setSavingRoute] = useState(false);
  useEffect(() => {
    setRoute({
      pickup_city: lead.pickup_city || "", pickup_state: lead.pickup_state || "", pickup_address: lead.pickup_address || "",
      delivery_city: lead.delivery_city || "", delivery_state: lead.delivery_state || "", delivery_address: lead.delivery_address || "",
    });
    // eslint-disable-next-line
  }, [lead.id]);
  const routeMissing = !String(route.pickup_city).trim() || !String(route.delivery_city).trim();
  const saveRoute = async () => {
    setSavingRoute(true);
    try { await api.patch(`/leads/${lead.id}/route`, route); toast.success("Route saved"); refresh(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingRoute(false); }
  };

  const seed = useCallback(() => {
    const qn = lead.quotation;
    const stored = (qn?.line_items || []).filter((li) => !String(li.description || "").toUpperCase().startsWith("GST"));
    const amtFor = (prefix, fallback) => {
      const m = stored.find((li) => String(li.description || "").startsWith(prefix));
      return m ? (m.amount ?? 0) : fallback;
    };
    const declaredTotal = (lead.items || []).reduce((a, it) => a + (Number(it.value) || 0), 0);
    const eligible = declaredTotal || Number(lead.declared_value) || 0;
    const pct = qn?.insurance_percent != null ? qn.insurance_percent : 2;
    const risk = Math.round(eligible * (Number(pct) || 0) / 100);
    const lift = lead.origin_lift ? "lift available" : "no lift";
    const dStored = stored.find((li) => /^(Dismantling|Handyman)/i.test(String(li.description || "")));
    const dMode = dStored && /service/i.test(dStored.description) ? "Service" : "By Party";
    const dAmt = dStored ? (dStored.amount ?? 0) : 0;
    const std = [
      { description: "Transportation Charges (HHG)", amount: amtFor("Transportation Charges", q.vendor_bid || 0), std: true },
      { description: "Professional Packing & Material Charges", amount: amtFor("Professional Packing", 0), std: true },
      { description: `Loading / Unloading Charges (Origin floor ${lead.origin_floor || "GF"}, ${lift})`, amount: amtFor("Loading / Unloading", 0), std: true },
      { description: "Unpacking Charges", amount: amtFor("Unpacking", 0), std: true },
      { description: `Risk / Transit Insurance Charges @ ${qn?.insurance_percent != null ? qn.insurance_percent : 2}%`, amount: amtFor("Risk / Transit", risk), std: true },
      { description: "Escort Charges", amount: amtFor("Escort", 0), std: true },
      { description: "Octroi / Union Charges", amount: amtFor("Octroi", 0), std: true },
      { description: "Warehousing Charges", amount: amtFor("Warehousing", 0), std: true },
      { description: `Handyman Services & Re-fixing Charges (${dMode})`, amount: dMode === "By Party" ? 0 : dAmt, std: true },
    ];
    const stdPrefixes = ["Transportation Charges", "Professional Packing", "Loading / Unloading", "Unpacking", "Risk / Transit", "Escort", "Octroi", "Warehousing", "Dismantling", "Handyman Services"];
    const custom = stored.filter((li) => !stdPrefixes.some((p) => String(li.description || "").startsWith(p))).map((li) => ({ description: li.description || "", amount: li.amount ?? 0 }));
    return {
      line_items: [...std, ...custom],
      packing_date: qn?.packing_date || lead.packing_date || "",
      moving_date: qn?.moving_date || lead.moving_date || lead.move_date || "",
      delivery_date: qn?.delivery_date || lead.delivery_date || "",
      remarks: qn?.remarks || "",
      validity: qn?.validity ?? 15,
      insured_value: qn?.insured_value != null ? qn.insured_value : eligible,
      insurance_percent: qn?.insurance_percent != null ? qn.insurance_percent : 2,
      party_name: qn?.party_name || "Consigner",
    };
    // eslint-disable-next-line
  }, [lead]);

  const [form, setForm] = useState(seed);
  useEffect(() => { setForm(seed()); }, [seed]);

  const vendorQuote = q.vendor_bid || 0;
  const mv = parseFloat(markupValue) || 0;
  const markupAmount = markupType === "percent" ? Math.round(vendorQuote * mv / 100) : mv;
  const transportCharge = vendorQuote + markupAmount;
  const firstMarkupRun = useRef(true);
  useEffect(() => {
    // Optional helper: only auto-fill Transportation when the markup/vendor inputs change,
    // NOT on mount — so a manually-typed Transportation amount is preserved across reloads.
    if (firstMarkupRun.current) { firstMarkupRun.current = false; return; }
    setForm((f) => ({ ...f, line_items: f.line_items.map((li) => (li.description || "").startsWith("Transportation Charges") ? { ...li, amount: transportCharge } : li) }));
    // eslint-disable-next-line
  }, [transportCharge]);

  const firstInsuredRun = useRef(true);
  useEffect(() => {
    if (firstInsuredRun.current) { firstInsuredRun.current = false; return; }
    const pct = Number(form.insurance_percent) || 0;
    const risk = Math.round((parseFloat(form.insured_value) || 0) * pct / 100);
    setForm((f) => ({ ...f, line_items: f.line_items.map((li) => (li.description || "").startsWith("Risk / Transit") ? { ...li, description: `Risk / Transit Insurance Charges @ ${pct}%`, amount: risk } : li) }));
    // eslint-disable-next-line
  }, [form.insured_value, form.insurance_percent]);

  const subtotal = form.line_items.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
  const gst = gstEnabled ? Math.round(subtotal * 0.05) : 0;
  const total = subtotal + gst;
  const setLI = (i, key, v) => setForm((f) => ({ ...f, line_items: f.line_items.map((li, idx) => (idx === i ? { ...li, [key]: v } : li)) }));
  const addRow = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", amount: 0 }] }));
  const delRow = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [transportMode, setTransportMode] = useState("HHG");
  const [customTransport, setCustomTransport] = useState("");
  const setTransport = (t) => setForm((f) => ({ ...f, line_items: f.line_items.map((li) => (li.description.startsWith("Transportation Charges") ? { ...li, description: `Transportation Charges (${t})` } : li)) }));
  const setDismantle = (m) => setForm((f) => ({ ...f, line_items: f.line_items.map((li) => (/^(Dismantling|Handyman)/i.test(li.description || "") ? { ...li, description: `Handyman Services & Re-fixing Charges (${m})`, amount: m === "By Party" ? 0 : li.amount } : li)) }));

  // ----- Two-vendor split fulfilment (Household = Vendor 1, Car = Vendor 2) -----
  const blankSplit = { car_enabled: false, car_vendor_id: "", car_vendor_name: "", car_amount: "", car_terms: "", car_delivery: "", hhg_terms: "", hhg_delivery: "" };
  const [split, setSplit] = useState(() => ({ ...blankSplit, ...(lead.quotation?.split_quote || {}) }));
  useEffect(() => { setSplit({ ...blankSplit, ...(lead.quotation?.split_quote || {}) }); /* eslint-disable-next-line */ }, [lead.id]);
  const hhgVendorName = (lead.bids || []).find((b) => b.vendor_id === lead.winning_vendor_id)?.vendor_name || lead.whatsapp_winner?.vendor_name || "Vendor 1 (HHG)";
  const vendorOpts = (() => {
    const map = new Map();
    (lead.bids || []).forEach((b) => { if (b.vendor_name) map.set(b.vendor_name, { name: b.vendor_name, id: b.vendor_id || "", amount: b.amount ?? b.quoted_price ?? b.vendor_bid ?? 0 }); });
    (lead.whatsapp_quotes || []).forEach((q) => { if (q.vendor_name && !map.has(q.vendor_name)) map.set(q.vendor_name, { name: q.vendor_name, id: "", amount: q.rate ?? 0 }); });
    if (lead.whatsapp_winner?.vendor_name && !map.has(lead.whatsapp_winner.vendor_name)) map.set(lead.whatsapp_winner.vendor_name, { name: lead.whatsapp_winner.vendor_name, id: "", amount: lead.whatsapp_winner.rate ?? 0 });
    return [...map.values()];
  })();
  const winnerBidFor = (ci, vid) => (lead.bids || []).find((b) => (b.consignment_idx || 0) === ci && b.vendor_id === vid);
  const hhgWinnerBid = winnerBidFor(0, lead.winning_vendor_id);
  const hhgAmount = hhgWinnerBid?.quoted_price ?? lead.whatsapp_winner?.rate ?? (q.vendor_bid || 0);
  const carCons = 1;
  const carSelId = (lead.consignments || [])[carCons]?.winning_vendor_id || (lead.consignments || [])[carCons]?.selected_vendor_id;
  const carWinnerBid = carSelId ? winnerBidFor(carCons, carSelId) : null;
  useEffect(() => {
    if (carWinnerBid && !split.car_vendor_name) {
      setSplit((s) => ({ ...s, car_enabled: true, car_vendor_name: carWinnerBid.vendor_name || "", car_vendor_id: carWinnerBid.vendor_id || "", car_amount: s.car_amount || (carWinnerBid.quoted_price ?? "") }));
    }
    // eslint-disable-next-line
  }, [carWinnerBid?.vendor_id]);
  const appendTerms = (remarks, terms) => {
    const cleaned = (remarks || "").split("\n").filter((l) => !l.startsWith("Vendors —")).join("\n").trim();
    const block = "Vendors — " + terms.join(" | ");
    return cleaned ? `${cleaned}\n${block}` : block;
  };
  const mergeQuotes = () => {
    const carAmt = parseFloat(split.car_amount) || 0;
    const carDesc = `Car Transportation Charges — Vendor: ${split.car_vendor_name || "Vendor 2"}`;
    setForm((f) => {
      let items = f.line_items.map((li) => {
        if ((li.description || "").startsWith("Transportation Charges")) {
          const base = li.description.replace(/\s*—\s*Vendor:.*$/, "");
          return { ...li, description: `${base} — Vendor: ${hhgVendorName}` };
        }
        return li;
      });
      const idx = items.findIndex((li) => /^Car Transportation Charges/i.test(li.description || ""));
      if (split.car_enabled && (carAmt > 0 || split.car_vendor_name)) {
        if (idx >= 0) items[idx] = { ...items[idx], description: carDesc, amount: carAmt };
        else items = [...items, { description: carDesc, amount: carAmt }];
      } else if (idx >= 0) {
        items = items.filter((_, i) => i !== idx);
      }
      const terms = [`${hhgVendorName} (HHG): ${split.hhg_terms || "—"}${split.hhg_delivery ? `, ETA ${split.hhg_delivery}` : ""}`];
      if (split.car_enabled && split.car_vendor_name) terms.push(`${split.car_vendor_name} (Car): ${split.car_terms || "—"}${split.car_delivery ? `, ETA ${split.car_delivery}` : ""}`);
      return { ...f, line_items: items, remarks: appendTerms(f.remarks, terms) };
    });
    toast.success("Vendor quotes merged — household & car itemised under their vendors");
  };

  const svcEmoji = (d) => {
    const s = (d || "").toLowerCase();
    if (s.includes("transport")) return "🚚";
    if (s.includes("packing")) return "📦";
    if (s.includes("loading") || s.includes("unloading")) return "⬆️";
    if (s.includes("unpack")) return "📤";
    if (s.includes("risk") || s.includes("insurance")) return "🛡️";
    if (s.includes("handyman") || s.includes("dismantl")) return "🔧";
    if (s.includes("escort")) return "🚗";
    if (s.includes("warehous")) return "🏬";
    if (s.includes("octroi") || s.includes("union") || s.includes("gst")) return "🧾";
    return "•";
  };

  const waCustomerLink = () => {
    const digits = String(lead.customer_phone || "").replace(/\D/g, "");
    const num = digits.length === 10 ? "91" + digits : digits;
    const msg = `*Arrow Logistics* — Quotation for Order ${lead.order_number}\n📍 ${lead.pickup_city} → ${lead.delivery_city}\n\n💰 *Total: ₹${total.toLocaleString("en-IN")}*\n\n📄 Please find your detailed quotation attached.\n👉 View & approve online: ${trackLink}`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  const winnerBid = (lead.bids || []).find((b) => b.vendor_id === lead.winning_vendor_id);
  const winnerName = winnerBid?.vendor_name || lead.whatsapp_winner?.vendor_name || "";
  const waVendorConfirmLink = () => {
    const digits = String(winnerBid?.phone || "").replace(/\D/g, "");
    const num = digits.length === 10 ? "91" + digits : digits;
    const msg = `*Arrow Logistics* — Vendor Confirmation for Order ${lead.order_number}\nYou have been confirmed as the vendor for this shipment (${lead.pickup_city} → ${lead.delivery_city}). Please proceed — LR & pickup details to follow.`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  const save = async () => {
    if (routeMissing) return toast.error("Enter Origin & Destination (From / To) first");
    if (!form.line_items.length) return toast.error("Add at least one line item");
    try {
      await api.patch(`/leads/${lead.id}/quotation`, {
        line_items: [...form.line_items.filter((li) => li.std || (parseFloat(li.amount) || 0) > 0 || (li.description || "").trim()).map((li) => ({ description: li.description, amount: parseFloat(li.amount) || 0 })), ...(gstEnabled ? [{ description: "GST @ 5%", amount: gst }] : [])],
        insured_value: parseFloat(form.insured_value) || 0,
        insurance_percent: parseFloat(form.insurance_percent) || 0,
        party_name: (form.party_name || "").trim(),
        packing_date: form.packing_date, moving_date: form.moving_date, delivery_date: form.delivery_date,
        remarks: form.remarks, validity: parseInt(form.validity) || 15,
        markup_type: markupType, markup_value: parseFloat(markupValue) || 0, gst_enabled: gstEnabled,
        split_quote: { ...split, car_amount: parseFloat(split.car_amount) || 0 },
      });
      toast.success("Quotation Updated Successfully");
      refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/leads/${lead.id}/documents/quotation`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `${lead.order_number}-quotation.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const downloadWaPdf = async () => {
    setWaBusy(true);
    try {
      const res = await api.get(`/leads/${lead.id}/documents/quotation?hide_items=true`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `${lead.order_number}-quotation-customer.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success("Customer quotation (no item list) downloaded — attach it in your WhatsApp chat");
    } catch (e) { toast.error(apiErr(e)); } finally { setWaBusy(false); }
  };

  return (
    <section id="quote-panel" className="border-2 border-primary/40 p-4 bg-muted/40" data-testid="quote-panel">
      <SectionTitle icon={Trophy}>Quotation {lead.quote_status !== "none" ? "· Editable" : ""}</SectionTitle>
      {!hasWinner && (
        <div className="mb-3 border border-amber-400/50 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs p-2.5" data-testid="quote-no-winner-hint">
          Tip: select a winning bid to auto-fill the transport cost — you can still add or edit line items manually below.
        </div>
      )}

      <div className={`mb-3 border p-3 ${routeMissing ? "border-red-500/60 bg-red-500/10" : "border-border bg-card"}`} data-testid="quote-route-block">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-bold uppercase tracking-wide flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" /> Origin &amp; Destination (From / To)</Label>
          {routeMissing && <span className="text-[11px] font-bold text-red-600 dark:text-red-400" data-testid="route-missing-warn">Required before quotation</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">From — City</Label>
            <PlacesAutocomplete className="bg-card h-9" value={route.pickup_city} placeholder="Origin city" disabled={frozen} testid="route-pickup-city" onChange={(v) => setRoute((r) => ({ ...r, pickup_city: v }))} onSelect={(p) => setRoute((r) => ({ ...r, pickup_city: p.city || p.formatted_address, pickup_state: p.state || r.pickup_state }))} />
            <Input className="rounded-none bg-card h-8 text-xs" value={route.pickup_state} onChange={(e) => setRoute((r) => ({ ...r, pickup_state: e.target.value }))} placeholder="State" disabled={frozen} data-testid="route-pickup-state" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">To — City</Label>
            <PlacesAutocomplete className="bg-card h-9" value={route.delivery_city} placeholder="Destination city" disabled={frozen} testid="route-delivery-city" onChange={(v) => setRoute((r) => ({ ...r, delivery_city: v }))} onSelect={(p) => setRoute((r) => ({ ...r, delivery_city: p.city || p.formatted_address, delivery_state: p.state || r.delivery_state }))} />
            <Input className="rounded-none bg-card h-8 text-xs" value={route.delivery_state} onChange={(e) => setRoute((r) => ({ ...r, delivery_state: e.target.value }))} placeholder="State" disabled={frozen} data-testid="route-delivery-state" />
          </div>
        </div>
        <Button size="sm" variant="outline" className="rounded-none mt-2 text-xs gap-1 h-8" onClick={saveRoute} disabled={savingRoute || frozen} data-testid="save-route-btn">{savingRoute ? "Saving…" : "Save Route"}</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        {(lead.consignment_count || 1) < 2 ? (
          <div className="space-y-1"><Label className="text-xs">Transportation Type</Label>
            <Select value={transportMode} onValueChange={(v) => { setTransportMode(v); setTransport(v === "Custom" ? (customTransport || "Custom") : v); }} disabled={frozen}><SelectTrigger className="rounded-none bg-card h-9" data-testid="quote-transport-type"><SelectValue placeholder="HHG" /></SelectTrigger>
              <SelectContent>{["HHG", "Car", "Office", "DTD", "Custom"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            {transportMode === "Custom" && <Input className="rounded-none bg-card h-9 mt-1.5" value={customTransport} onChange={(e) => { setCustomTransport(e.target.value); setTransport(e.target.value || "Custom"); }} disabled={frozen} placeholder="Enter transport type…" data-testid="quote-transport-custom" />}</div>
        ) : (
          <div className="space-y-1"><Label className="text-xs">Transportation Type</Label>
            <div className="min-h-9 flex items-center px-2 py-1.5 border border-border bg-muted text-[11px] text-muted-foreground" data-testid="quote-transport-type-moved">Set per consignment in Booking Type · {(lead.consignments || []).map((c, i) => `C${i + 1}: ${c.transport_type || "—"}`).join("  ·  ") || "not set"}</div></div>
        )}
        <div className="space-y-1"><Label className="text-xs">Handyman Services</Label>
          <Select onValueChange={setDismantle} defaultValue="By Party"><SelectTrigger className="rounded-none bg-card h-9" data-testid="quote-dismantle-mode"><SelectValue placeholder="By Party" /></SelectTrigger>
            <SelectContent>{["By Party", "Service"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
      </div>

      <div className="border border-primary/40 bg-card p-3 mb-3" data-testid="insured-value-block">
        <Label className="text-xs font-bold uppercase tracking-wide flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Risk / Transit Insurance</Label>
        <div className="grid sm:grid-cols-3 gap-3 mt-1.5">
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Value of Insured Items (₹)</Label>
            <Input type="number" min="0" className="rounded-none bg-background h-9 font-mono-num" value={form.insured_value} onChange={(e) => setField("insured_value", e.target.value)} disabled={frozen} placeholder="0" data-testid="quote-insured-value" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Insurance %</Label>
            <Input type="number" min="0" step="0.01" className="rounded-none bg-background h-9 font-mono-num" value={form.insurance_percent} onChange={(e) => setField("insurance_percent", e.target.value)} disabled={frozen} placeholder="2" data-testid="quote-insurance-percent" />
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Risk / Transit Insurance auto-calculates at the % above of the insured value (editable).</p>
        <div className="space-y-1 mt-3">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Party Name (printed on Quotation)</Label>
          <Input className="rounded-none bg-background h-9" value={form.party_name} onChange={(e) => setField("party_name", e.target.value)} disabled={frozen} placeholder="Consigner" data-testid="quote-party-name" />
        </div>
      </div>

      <div className="border border-primary/40 bg-card p-3 mb-3" data-testid="markup-section">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-bold uppercase tracking-wide flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5 text-primary" /> Apply Profit Markup</Label>
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Internal · vendor cost hidden from customer</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-[11px]">Vendor Quote</Label>
            <div className="h-9 flex items-center px-2 border border-border bg-muted font-mono-num text-sm" data-testid="markup-vendor-quote">{money(vendorQuote)}</div>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Markup Type</Label>
            <div className="flex">
              <button type="button" onClick={() => setMarkupType("fixed")} disabled={frozen} className={`flex-1 h-9 text-xs font-bold border transition-colors ${markupType === "fixed" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid="markup-type-fixed">₹ Fixed</button>
              <button type="button" onClick={() => setMarkupType("percent")} disabled={frozen} className={`flex-1 h-9 text-xs font-bold border transition-colors ${markupType === "percent" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid="markup-type-percent">% Percent</button>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Markup {markupType === "percent" ? "(%)" : "(₹)"}</Label>
            <Input type="number" min="0" className="rounded-none h-9 font-mono-num bg-background" value={markupValue} onChange={(e) => setMarkupValue(e.target.value)} disabled={frozen} placeholder="0" data-testid="markup-value" />
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between flex-wrap gap-1 border-t border-border pt-2">
          <span className="text-[11px] text-muted-foreground font-mono-num">{money(vendorQuote)} + {money(markupAmount)} markup =</span>
          <span className="font-mono-num font-bold text-primary text-sm" data-testid="markup-transport-charge">Transportation Charges: {money(transportCharge)}</span>
        </div>
      </div>

      {/* Two-vendor split fulfilment: Household (Vendor 1) + Car (Vendor 2) */}
      <div className="border border-primary/40 bg-card p-3 mb-3" data-testid="split-quote-section">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <Label className="text-xs font-bold uppercase tracking-wide flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5 text-primary" /> Car Transportation &amp; Vendor Split</Label>
          <Button type="button" size="sm" className="rounded-none gap-1 text-xs font-bold uppercase tracking-wide" onClick={mergeQuotes} disabled={frozen} data-testid="merge-quotes-btn">Merge Quotes</Button>
        </div>
        <p className="text-[11px] text-muted-foreground mb-2">Award household goods to Vendor 1 and the vehicle to Vendor 2. "Merge Quotes" itemises both under their assigned vendors in one quotation.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="border border-border p-2.5 space-y-1.5">
            <div className="text-[11px] font-bold uppercase tracking-wide">Vendor 1 · Household</div>
            <div className="text-xs text-muted-foreground">Selected Vendor: <span className="text-foreground font-medium" data-testid="split-hhg-vendor">{hhgVendorName}</span></div>
            <div className="text-xs text-muted-foreground">Amount: <span className="font-mono-num text-primary font-bold" data-testid="split-hhg-amount">{money(hhgAmount)}</span></div>
            <Input className="rounded-none bg-background h-8 text-xs" value={split.hhg_terms} onChange={(e) => setSplit((s) => ({ ...s, hhg_terms: e.target.value }))} disabled={frozen} placeholder="Terms (e.g. to-pay, 2 helpers)" data-testid="split-hhg-terms" />
            <Input className="rounded-none bg-background h-8 text-xs" value={split.hhg_delivery} onChange={(e) => setSplit((s) => ({ ...s, hhg_delivery: e.target.value }))} disabled={frozen} placeholder="Delivery timeline (e.g. 4-5 days)" data-testid="split-hhg-delivery" />
          </div>
          <div className="border border-border p-2.5 space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-bold uppercase tracking-wide">Vendor 2 · Car</div>
              <label className="flex items-center gap-1 text-[11px] cursor-pointer"><input type="checkbox" checked={!!split.car_enabled} onChange={(e) => setSplit((s) => ({ ...s, car_enabled: e.target.checked }))} disabled={frozen} data-testid="split-car-enabled" /> Include</label>
            </div>
            {carWinnerBid && (
              <div className="border border-emerald-500/40 bg-emerald-500/5 p-2 text-xs" data-testid="split-car-selected">
                <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400">Selected via Bidding</div>
                <div className="text-muted-foreground">Vendor: <span className="font-medium text-foreground" data-testid="split-car-selected-vendor">{carWinnerBid.vendor_name}</span></div>
                <div className="text-muted-foreground">Amount: <span className="font-mono-num text-primary font-bold" data-testid="split-car-selected-amount">{money(carWinnerBid.quoted_price ?? 0)}</span></div>
              </div>
            )}
            <select className="rounded-none bg-background border border-border h-8 text-xs w-full px-2" value={split.car_vendor_name} onChange={(e) => { const o = vendorOpts.find((v) => v.name === e.target.value); setSplit((s) => ({ ...s, car_vendor_name: e.target.value, car_vendor_id: o?.id || "", car_amount: s.car_amount || (o?.amount || "") })); }} disabled={frozen} data-testid="split-car-vendor">
              <option value="">— Car Vendor —</option>
              {vendorOpts.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
            </select>
            <Input type="number" min="0" className="rounded-none bg-background h-8 text-xs font-mono-num" value={split.car_amount} onChange={(e) => setSplit((s) => ({ ...s, car_amount: e.target.value }))} disabled={frozen} placeholder="Car transportation cost ₹" data-testid="split-car-amount" />
            <Input className="rounded-none bg-background h-8 text-xs" value={split.car_terms} onChange={(e) => setSplit((s) => ({ ...s, car_terms: e.target.value }))} disabled={frozen} placeholder="Terms" data-testid="split-car-terms" />
            <Input className="rounded-none bg-background h-8 text-xs" value={split.car_delivery} onChange={(e) => setSplit((s) => ({ ...s, car_delivery: e.target.value }))} disabled={frozen} placeholder="Delivery timeline" data-testid="split-car-delivery" />
          </div>
        </div>
      </div>

      <div className="border border-border bg-card">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
          <div className="col-span-8">Description</div><div className="col-span-3 text-right">Amount ₹</div><div className="col-span-1" />
        </div>
        {form.line_items.map((li, i) => {
          const isTransport = (li.description || "").startsWith("Transportation Charges");
          const isDismantleParty = /^(Dismantling|Handyman)/i.test(li.description || "") && /by party/i.test(li.description);
          return (
          <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b border-border last:border-0" data-testid={`quote-line-${i}`}>
            {li.std ? (
              <div className="col-span-8 h-9 flex items-center text-sm font-medium px-1" data-testid={`quote-line-desc-${i}`}>
                {li.description}
                {isTransport && <span className="ml-1 text-[10px] text-muted-foreground uppercase">· auto · editable</span>}
                {isDismantleParty && <span className="ml-1 text-[10px] text-muted-foreground uppercase">· by party</span>}
              </div>
            ) : (
              <Input className="col-span-8 rounded-none h-9 bg-background" value={li.description} onChange={(e) => setLI(i, "description", e.target.value)} disabled={frozen} placeholder="e.g. Additional service charge" data-testid={`quote-line-desc-${i}`} />
            )}
            <Input type="number" className="col-span-3 rounded-none h-9 bg-background font-mono-num text-right" value={isDismantleParty ? 0 : li.amount} onChange={(e) => setLI(i, "amount", e.target.value)} disabled={frozen || isDismantleParty} data-testid={`quote-line-amount-${i}`} />
            {li.std ? <div className="col-span-1" /> : <Button size="icon" variant="ghost" className="col-span-1 rounded-none h-9 w-9 text-destructive" onClick={() => delRow(i)} disabled={frozen} data-testid={`quote-line-del-${i}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
          );
        })}
        <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b border-border bg-muted/40" data-testid="quote-gst-row">
          <label className="col-span-8 text-sm font-medium flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={gstEnabled} onChange={(e) => setGstEnabled(e.target.checked)} disabled={frozen} className="h-4 w-4 accent-primary" data-testid="quote-gst-toggle" />
            GST @ 5% {gstEnabled ? "" : <span className="text-muted-foreground text-xs">(not applied)</span>}
          </label>
          <div className="col-span-3 text-right font-mono-num text-sm">{gstEnabled ? money(gst) : "—"}</div>
          <div className="col-span-1" />
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t border-border">
          <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs" onClick={addRow} disabled={frozen} data-testid="quote-add-row"><Plus className="h-3.5 w-3.5" /> Add Description</Button>
          <div className="font-mono-num font-bold text-sm">Total: <span className="text-primary" data-testid="quote-grand-total">{money(total)}</span></div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-3">
        <div className="space-y-1.5"><Label className="text-xs">Packing date</Label><Input type="date" className="rounded-none bg-card h-9" value={form.packing_date} onChange={(e) => setField("packing_date", e.target.value)} disabled={frozen} data-testid="quote-packing-date" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Moving date</Label><Input type="date" className="rounded-none bg-card h-9" value={form.moving_date} onChange={(e) => setField("moving_date", e.target.value)} disabled={frozen} data-testid="quote-moving-date" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Delivery date</Label><Input type="date" className="rounded-none bg-card h-9" value={form.delivery_date} onChange={(e) => setField("delivery_date", e.target.value)} disabled={frozen} data-testid="quote-delivery-date" /></div>
      </div>
      <div className="grid sm:grid-cols-4 gap-3 mt-3">
        <div className="sm:col-span-3 space-y-1.5"><Label className="text-xs">Remarks</Label><Textarea rows={2} className="rounded-none bg-card" value={form.remarks} onChange={(e) => setField("remarks", e.target.value)} disabled={frozen} placeholder="Any special notes to print on the quotation…" data-testid="quote-remarks" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Valid (days)</Label><Input type="number" className="rounded-none bg-card h-9 font-mono-num" value={form.validity} onChange={(e) => setField("validity", e.target.value)} disabled={frozen} data-testid="quote-validity" /></div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button className="rounded-none font-bold uppercase tracking-wide" onClick={save} disabled={frozen || routeMissing} title={routeMissing ? "Enter Origin & Destination first" : ""} data-testid="generate-quote-btn">{lead.quote_status === "none" ? "Make Quotation" : "Update Quotation"}</Button>
        <Button variant="outline" className="rounded-none gap-2" onClick={downloadPdf} disabled={busy} data-testid="download-quotation-pdf"><Download className="h-4 w-4" /> {busy ? "Generating…" : "Download Quotation PDF"}</Button>
        <Button variant="outline" className="rounded-none gap-2 border-emerald-500/50 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10" onClick={downloadWaPdf} disabled={waBusy} title="Same quotation with company profile, but without the item list — for sending to the customer on WhatsApp" data-testid="download-whatsapp-quotation-pdf"><MessageCircle className="h-4 w-4" /> {waBusy ? "Generating…" : "WhatsApp Quotation PDF (No Items)"}</Button>
      </div>

      {lead.quote_status !== "none" && (
        <div className="mt-4 space-y-3 border-t border-border pt-3">
          <div className="flex items-center gap-2">
            <Input readOnly value={trackLink} className="rounded-none font-mono-num text-xs" data-testid="lead-track-link" />
            <Button variant="outline" size="icon" className="rounded-none shrink-0" onClick={() => { navigator.clipboard.writeText(trackLink); toast.success("Customer link copied"); }}><Copy className="h-4 w-4" /></Button>
            <StatusBadge status={frozen ? "confirmed_dispatched" : lead.quote_status} />
          </div>
          <a href={waCustomerLink()} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full h-11 bg-emerald-500 text-white font-bold uppercase tracking-wide hover:bg-emerald-600 transition-colors" data-testid="send-quote-customer-btn">
            <MessageCircle className="h-4 w-4" /> Send WhatsApp Quote
          </a>
          {lead.quote_status === "approved" && (winnerName || lead.winning_vendor_id) && (
            <div className="border border-primary/40 bg-primary/5 p-2.5" data-testid="vendor-confirm-block">
              <p className="text-[11px] text-muted-foreground mb-2">Quotation is <span className="font-bold text-primary">Approved</span>. You can now notify the winning vendor{winnerName ? ` — ${winnerName}` : ""}.</p>
              <a href={waVendorConfirmLink()} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full h-11 border border-primary text-primary font-bold uppercase tracking-wide hover:bg-primary/10 transition-colors" data-testid="confirm-winning-vendor-btn">
                <MessageCircle className="h-4 w-4" /> Send WhatsApp Confirmation to Winning Vendor
              </a>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/* ---------------- Delivery & Feedback ---------------- */
function DeliveryPanel({ lead, refresh, frozen }) {
  if (lead.order_state !== "confirmed_dispatched" && lead.status !== "confirmed") return null;
  const feedbackLink = lead.feedback_token ? `${window.location.origin}/feedback/${lead.feedback_token}` : "";

  const deliver = async () => {
    try { await api.post(`/leads/${lead.id}/deliver`); toast.success("Order marked as delivered"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const waDeliveryLink = () => {
    const digits = String(lead.customer_phone || "").replace(/\D/g, "");
    const num = digits.length === 10 ? "91" + digits : digits;
    const msg = `*Arrow Logistics* — Delivery Complete ✅\nYour order ${lead.order_number}${lead.lr_number ? ` (LR ${lead.lr_number})` : ""} (${lead.pickup_city} → ${lead.delivery_city}) has been delivered.\nWe'd love your feedback — please rate your experience here: ${feedbackLink}`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <section className="border border-border p-4 bg-muted/40">
      <SectionTitle icon={PackageCheck}>Delivery &amp; Feedback</SectionTitle>
      {!lead.delivered_at ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Mark this order delivered to generate a customer feedback link and enable the WhatsApp "Delivery Complete" message.</p>
          <Button className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={deliver} data-testid="mark-delivered-btn"><PackageCheck className="h-4 w-4" /> Mark Delivered</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-widest text-emerald-500 font-bold" data-testid="delivered-badge">Delivered · {new Date(lead.delivered_at).toLocaleString()}</div>
          <div className="flex items-center gap-2">
            <Input readOnly value={feedbackLink} className="rounded-none font-mono-num text-xs" data-testid="feedback-link" />
            <Button variant="outline" size="icon" className="rounded-none shrink-0" onClick={() => { navigator.clipboard.writeText(feedbackLink); toast.success("Feedback link copied"); }}><Copy className="h-4 w-4" /></Button>
          </div>
          <a
            href={waDeliveryLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full h-11 bg-emerald-500 text-white font-bold uppercase tracking-wide hover:bg-emerald-600 transition-colors"
            data-testid="send-delivery-complete-btn"
          >
            <MessageCircle className="h-4 w-4" /> Send "Delivery Complete" on WhatsApp
          </a>
          {lead.feedback && (
            <div className="border border-border bg-card p-3" data-testid="lead-feedback">
              <div className="flex items-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= lead.feedback.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />)}
                <span className="ml-2 text-sm font-bold">{lead.feedback.rating}/5</span>
              </div>
              {lead.feedback.comment && <p className="text-sm text-muted-foreground italic">"{lead.feedback.comment}"</p>}
              <div className="text-[11px] text-muted-foreground mt-1">Partner: {lead.partner_vendor_name || "—"}</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}


/* ---------------- Generate Documents ---------------- */
function DocumentsPanel({ lead }) {
  const [busy, setBusy] = useState("");
  const [withLetterhead, setWithLetterhead] = useState(true);
  const download = async (type, endpoint, filename) => {
    setBusy(type);
    const url = withLetterhead ? endpoint : endpoint + (endpoint.includes("?") ? "&" : "?") + "letterhead=false";
    try {
      const res = await api.get(url, { responseType: "blob" });
      const dl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = dl; a.download = filename; a.click();
      URL.revokeObjectURL(dl);
      toast.success("Document generated");
    } catch (e) {
      let msg = apiErr(e);
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) {}
      toast.error(msg);
    } finally { setBusy(""); }
  };
  const on = lead.order_number;
  const [inv, setInv] = useState({ po_number: "", po_date: "", hsn_code: "" });
  const genInvoice = async () => {
    setBusy("invoice");
    const qs = new URLSearchParams({ po_number: inv.po_number, po_date: inv.po_date, hsn_code: inv.hsn_code, ...(withLetterhead ? {} : { letterhead: "false" }) }).toString();
    try {
      const res = await api.get(`/leads/${lead.id}/documents/invoice?${qs}`, { responseType: "blob" });
      const dl = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = dl; a.download = `${on}-invoice.pdf`; a.click(); URL.revokeObjectURL(dl);
      toast.success("Invoice generated");
    } catch (e) {
      let msg = apiErr(e);
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) {}
      toast.error(msg);
    } finally { setBusy(""); }
  };
  const docs = [
    { type: "challan", icon: Truck, label: "Lorry Freight Challan", ep: `/leads/${lead.id}/documents/challan`, fn: `${on}-challan.pdf` },
    { type: "quotation", icon: FileText, label: "Quotation + Company Profile", ep: `/leads/${lead.id}/documents/quotation`, fn: `${on}-quotation.pdf` },
    { type: "manifest", icon: ClipboardList, label: "Consignment Manifest (Vendor)", ep: `/leads/${lead.id}/documents/manifest`, fn: `${on}-manifest.pdf` },
  ];
  return (
    <section className="border-2 border-primary/40 p-4 bg-primary/5" data-testid="documents-panel">
      <SectionTitle icon={FileDown}>Generate Document</SectionTitle>
      <p className="text-xs text-muted-foreground mb-3">Download branded PDFs for this order. The Quotation automatically appends the Arrow Company Profile.</p>
      <label className="flex items-center gap-2 mb-3 text-xs cursor-pointer select-none">
        <Switch checked={withLetterhead} onCheckedChange={setWithLetterhead} data-testid="letterhead-toggle" />
        <span className="font-medium">Print on Arrow letterhead</span>
        <span className="text-muted-foreground">{withLetterhead ? "(on)" : "(off — for pre-printed stationery)"}</span>
      </label>
      <div className="grid sm:grid-cols-2 gap-2">
        {docs.map((d) => (
          <Button key={d.type} variant="outline" className="rounded-none justify-start gap-2 h-11"
            onClick={() => download(d.type, d.ep, d.fn)} disabled={!!busy} data-testid={`gen-doc-${d.type}`}>
            <d.icon className="h-4 w-4 text-primary" /> {busy === d.type ? "Generating…" : d.label}
          </Button>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Expense Vouchers ---------------- */
function ExpensesPanel({ lead, refresh }) {
  const { user } = useAuth();
  const [data, setData] = useState({ expenses: [], total: 0 });
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const load = useCallback(() => {
    api.get(`/leads/${lead.id}/expenses`).then((r) => setData(r.data)).catch(() => {});
  }, [lead.id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!category.trim()) return toast.error("Enter a category");
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post(`/leads/${lead.id}/expenses`, { category: category.trim(), amount: amt, remarks: remarks.trim(), order_number: lead.order_number, lr_number: lead.lr_number || "" });
      toast.success("Expense added"); setCategory(""); setAmount(""); setRemarks(""); load(); refresh?.();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/leads/${lead.id}/expenses/${id}`); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const voucher = async (exp) => {
    try {
      const res = await api.get(`/expenses/${exp.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${exp.serial.replace(/\//g, "-")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <section className="border border-border p-4" data-testid="expenses-panel">
      <div className="flex items-center justify-between mb-3">
        <SectionTitle icon={IndianRupee}>Expenses</SectionTitle>
        <span className="font-mono-num text-sm font-bold text-primary" data-testid="expenses-total">{money(data.total)}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
        <div className="sm:col-span-4 space-y-1.5"><Label className="text-xs">Category</Label>
          <Input className="rounded-none bg-card h-9" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Fuel, Labour, Toll" data-testid="expense-category" /></div>
        <div className="sm:col-span-3 space-y-1.5"><Label className="text-xs">Amount ₹</Label>
          <Input type="number" className="rounded-none bg-card h-9 font-mono-num" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="expense-amount" /></div>
        <div className="sm:col-span-4 space-y-1.5"><Label className="text-xs">Remarks</Label>
          <Input className="rounded-none bg-card h-9" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="optional" data-testid="expense-remarks" /></div>
        <div className="sm:col-span-1"><Button className="rounded-none w-full h-9 px-0" onClick={add} data-testid="add-expense-btn"><Plus className="h-4 w-4" /></Button></div>
      </div>
      <div className="border border-border divide-y divide-border mt-3">
        {data.expenses.length === 0 && <div className="p-3 text-sm text-muted-foreground">No expenses recorded.</div>}
        {data.expenses.map((e) => (
          <div key={e.id} className="flex items-center gap-2 p-2 px-3 text-sm" data-testid={`expense-row-${e.id}`}>
            <div className="flex-1">
              <div className="font-medium">{e.category} <span className="font-mono-num text-muted-foreground text-xs">· {e.serial}</span></div>
              {e.remarks && <div className="text-xs text-muted-foreground">{e.remarks}</div>}
            </div>
            <span className="font-mono-num font-bold">{money(e.amount)}</span>
            <Button size="icon" variant="ghost" className="rounded-none h-8 w-8" title="Download voucher" onClick={() => voucher(e)} data-testid={`expense-pdf-${e.id}`}><Download className="h-4 w-4" /></Button>
            {(user?.role === "admin" || user?.role === "manager") && (
              <Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-destructive" title="Delete" onClick={() => del(e.id)} data-testid={`expense-del-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Lorry Receipt (after Confirmed) ---------------- */
function VendorDocsPanel({ lead, refresh }) {
  const confirmed = lead.order_state === "confirmed_dispatched" || lead.status === "confirmed";
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const status = lead.vendor_docs_status;
  const docs = lead.vendor_docs;
  const token = lead.vendor_docs_token;
  const fileUrl = (p) => `${API}/files/${p}?auth=${localStorage.getItem("arrow_token") || ""}`;
  const publicLink = link || (token ? `${window.location.origin}/vendor-docs/${token}` : "");

  const request = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/leads/${lead.id}/request-vendor-docs`); setLink(`${window.location.origin}/vendor-docs/${data.token}`); toast.success("Vendor document link ready"); refresh(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const approve = async () => { try { await api.post(`/leads/${lead.id}/vendor-docs/approve`); toast.success("Documents verified"); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const reject = async () => { try { await api.post(`/leads/${lead.id}/vendor-docs/reject`); toast.success("Marked for re-submission"); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const verifyRC = (num) => {
    const clean = (num || "").replace(/\s/g, "");
    if (navigator.clipboard) navigator.clipboard.writeText(clean).catch(() => {});
    window.open(`https://vahan.parivahan.gov.in/nrservices/faces/user/citizen/citizenlogin.xhtml?vehicleNumber=${encodeURIComponent(clean)}`, "_blank", "noopener");
    toast.success("Vehicle no. copied — paste into Vahan to verify");
  };
  const waShare = () => {
    const msg = `*Arrow Logistics* — Please submit vehicle & driver documents for Order ${lead.order_number} (${lead.pickup_city} → ${lead.delivery_city}). Secure link (no login): ${publicLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  if (!confirmed) return null;

  const STATUS = {
    requested: { t: "Awaiting Submission", c: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" },
    pending_review: { t: "Docs Pending Review", c: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" },
    verified: { t: "Verified ✓", c: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" },
  };
  const st = STATUS[status];

  return (
    <section className="border border-border p-4" data-testid="vendor-docs-panel">
      <SectionTitle icon={ShieldCheck}>Vendor Documents</SectionTitle>
      <div className="flex items-center gap-2 mb-3">
        {st ? <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase ${st.c}`} data-testid="vendor-docs-status">{st.t}</span>
            : <span className="text-xs text-muted-foreground">No document request sent yet.</span>}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Button className="rounded-none gap-2 font-bold uppercase text-xs" onClick={request} disabled={busy} data-testid="request-vendor-docs-btn">
          <FileText className="h-4 w-4" /> {status ? "Regenerate Link" : "Request Vendor Docs"}
        </Button>
        {publicLink && (
          <>
            <Button variant="outline" className="rounded-none gap-2 text-xs" onClick={() => { navigator.clipboard.writeText(publicLink); toast.success("Link copied"); }} data-testid="copy-vendor-docs-link"><Copy className="h-4 w-4" /> Copy Link</Button>
            <Button variant="outline" className="rounded-none gap-2 text-xs" onClick={waShare} data-testid="wa-vendor-docs-link"><MessageCircle className="h-4 w-4" /> WhatsApp</Button>
          </>
        )}
      </div>

      {docs && (
        <div className="border border-border p-3 space-y-3 bg-muted/30" data-testid="vendor-docs-submitted">
          <div className="grid sm:grid-cols-2 gap-2 text-sm">
            <Info label="Vehicle Number" value={docs.vehicle_number} />
            <Info label="Chassis No." value={docs.chassis_no} />
            <Info label="Engine No." value={docs.engine_no} />
            <Info label="National Permit No." value={docs.national_permit_no} />
            <Info label="Driver DL No." value={docs.driver_dl_no} />
            <Info label="Driver Name" value={docs.driver_name} />
          </div>
          <div className="flex flex-wrap gap-3">
            {[["RC", docs.rc_photo], ["Permit", docs.permit_photo], ["Driver DL", docs.dl_photo]].map(([lbl, p]) => (
              <div key={lbl} className="text-center">
                <div className="text-[10px] uppercase text-muted-foreground mb-1">{lbl}</div>
                {p ? <a href={fileUrl(p)} target="_blank" rel="noreferrer" data-testid={`vendor-doc-photo-${lbl.toLowerCase().replace(/\s/g, "-")}`}><img src={fileUrl(p)} alt={lbl} className="h-24 w-24 object-cover border border-border hover:border-primary" /></a>
                   : <div className="h-24 w-24 grid place-items-center border border-dashed border-border text-[10px] text-muted-foreground">Not uploaded</div>}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="outline" className="rounded-none gap-2 text-xs" onClick={() => verifyRC(docs.vehicle_number)} data-testid="verify-rc-btn"><ExternalLink className="h-4 w-4" /> Verify RC (Vahan)</Button>
            {status !== "verified" && <Button className="rounded-none gap-2 text-xs font-bold uppercase bg-emerald-500 hover:bg-emerald-600 text-white" onClick={approve} data-testid="approve-vendor-docs-btn"><ShieldCheck className="h-4 w-4" /> Approve Docs</Button>}
            {status !== "requested" && <Button variant="ghost" className="rounded-none gap-2 text-xs text-destructive" onClick={reject} data-testid="reject-vendor-docs-btn">Reject / Re-request</Button>}
          </div>
          <p className="text-[11px] text-muted-foreground">Submitted details have auto-filled this order's Lorry Freight Challan.</p>
        </div>
      )}
    </section>
  );
}

function LorryReceiptPanel({ lead, refresh }) {
  const confirmed = lead.order_state === "confirmed_dispatched" || lead.status === "confirmed";
  const lrs = lead.lrs || (lead.lr ? [{ ...lead.lr, id: lead.lr.id || "legacy" }] : []);
  const [f, setF] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [vendors, setVendors] = useState([]);
  useEffect(() => { api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {}); }, []);
  const [companies, setCompanies] = useState([]);
  useEffect(() => { api.get("/customers").then((r) => setCompanies([...new Set((r.data || []).map((c) => c.company || c.name).filter(Boolean))])).catch(() => {}); }, []);

  const bidFor = (vid) => (lead.bids || []).find((b) => b.vendor_id === vid && b.quoted_price != null)?.quoted_price;
  const [activeTruck, setActiveTruck] = useState(0);

  const pickVendor = (vid) => {
    if (vid === "__manual__") { setF((p) => ({ ...p, vendor_id: "", vendor_name: "" })); return; }
    const v = vendors.find((x) => x.id === vid);
    if (!v) return;
    setF((p) => {
      const next = { ...p, vendor_id: v.id, vendor_name: v.name, carrier_name: v.name };
      const bid = bidFor(v.id);
      if (bid != null && !(Number(p.vendor_cost) > 0)) next.vendor_cost = bid;
      return next;
    });
  };

  const blank = () => ({
    carrier_name: "Arrow Packers & Trucking Pvt. Ltd.",
    vendor_id: "", vendor_name: "",
    vehicle_number: "", chassis_no: "", engine_no: "", national_permit_no: "", driver_dl_no: "", driver_name: "", driver_phone: "", gstin: "", eway_bill: "", execution_date: "",
    consignor: { name: lead.customer_name, address: `${lead.delivery_address}, ${lead.delivery_city}, ${lead.delivery_state}`, gstin: "" },
    consignee: { name: lead.customer_name, address: `${lead.delivery_address}, ${lead.delivery_city}, ${lead.delivery_state}`, gstin: "" },
    manifest: {}, financial: {}, legal: {},
    goods_type: "Transportation of Household Goods", custom_description: "", remarks: "",
    invoice_no: "", hsn_code: "", vendor_cost: "", consignment_idx: 0, bill_to: "",
    insurance_declared: "not_insured", insurance_company: "", insurance_policy_no: "", insurance_date: "", insurance_amount: "", insurance_risk: "",
  });

  const openNew = () => {
    setEditingId(null);
    consignorTouched.current = false;
    const primary = lrs[0];
    const nextCons = Math.min(lrs.length, Math.max(0, (lead.consignment_count || 1) - 1));
    if (primary) {
      // Smart auto-fill: copy customer info + to/from addresses from the primary LR
      setF({
        ...blank(),
        consignment_idx: nextCons,
        consignor: { ...blank().consignor, ...(primary.consignor || {}) },
        consignee: { ...blank().consignee, ...(primary.consignee || {}) },
      });
    } else {
      setF({ ...blank(), consignment_idx: 0 });
    }
  };
  const openEdit = (l) => {
    setEditingId(l.id);
    consignorTouched.current = true;
    setF({
      ...blank(), ...l,
      remarks: l.remarks || "",
      insurance_declared: l.insurance?.declared || "not_insured",
      insurance_company: l.insurance?.company || "", insurance_policy_no: l.insurance?.policy_no || "",
      insurance_date: l.insurance?.date || "", insurance_amount: l.insurance?.amount || "", insurance_risk: l.insurance?.risk || "",
    });
  };

  const [customOpen, setCustomOpen] = useState(false);
  const consignorTouched = useRef(false);
  if (!confirmed) return null;

  const setNested = (grp, key, val) => setF((p) => {
    const next = { ...p, [grp]: { ...p[grp], [key]: val } };
    if (grp === "consignor") consignorTouched.current = true;
    if (grp === "consignee" && !consignorTouched.current) next.consignor = { ...next.consignor, [key]: val };
    return next;
  });
  const setTop = (key, val) => setF((p) => ({ ...p, [key]: val }));

  const save = async () => {
    if (gstErr(f.gstin) || gstErr(f.consignor.gstin) || gstErr(f.consignee.gstin) || ewayErr(f.eway_bill)) {
      toast.error("Fix GSTIN / E-Way Bill format errors before saving"); return;
    }
    const num = (x) => (x === "" || x == null || isNaN(Number(x)) ? 0 : Number(x));
    const payload = {
      ...f,
      vendor_cost: num(f.vendor_cost),
      financial: {
        ...(f.financial || {}),
        basic_freight: num(f.financial?.basic_freight),
        handling: num(f.financial?.handling),
        green_tax: num(f.financial?.green_tax),
        declared_value: num(f.financial?.declared_value),
        insurance_premium: num(f.financial?.insurance_premium),
        advance_paid: num(f.financial?.advance_paid),
        office_mamul: num(f.financial?.office_mamul),
        detention_unloading: num(f.financial?.detention_unloading),
        balance: num(f.financial?.balance),
      },
    };
    try {
      if (editingId) await api.patch(`/leads/${lead.id}/lorry-receipt/${editingId}`, payload);
      else await api.post(`/leads/${lead.id}/lorry-receipt`, payload);
      toast.success(editingId ? "Lorry Receipt updated" : "New LR added");
      setF(null); setEditingId(null); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const download = async (l) => {
    try {
      const res = await api.get(`/leads/${lead.id}/lorry-receipt/pdf?lr_id=${l.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(l.lr_number || "LR").replace(/\//g, "-")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const downloadDoc = async (l, type) => {
    try {
      const res = await api.get(`/leads/${lead.id}/documents/${type}?lr_id=${l.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(l.lr_number || "LR").replace(/[\/ ]/g, "-")}-${type}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const removeLr = async (l) => {
    if (!window.confirm(`Delete LR ${l.lr_number}? This cannot be undone.`)) return;
    try { await api.delete(`/leads/${lead.id}/lorry-receipt/${l.id}`); toast.success("LR removed"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const sendTracking = async (l) => {    try {
      const r = await api.post(`/leads/${lead.id}/lorry-receipt/${l.id}/request-tracking`);
      const link = `${window.location.origin}/drive/${r.data.tracking_token}`;
      const digits = String(l.driver_phone || "").replace(/\D/g, "");
      const num = digits.length === 10 ? "91" + digits : digits;
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(`*Arrow Logistics* — Live location tracking for ${l.lr_number} (Order ${lead.order_number}). Please open this link and allow location access: ${link}`)}`, "_blank");
      toast.success("Tracking link ready — WhatsApp opened"); refresh();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setStatus = async (l, s) => {
    try { await api.post(`/leads/${lead.id}/lorry-receipt/${l.id}/shipment-status`, { shipment_status: s }); toast.success("Truck status updated"); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const SHIP = [["at_origin", "At Origin"], ["in_transit", "In Transit"], ["on_the_way", "On the way"], ["at_destination", "At Destination"], ["delivered", "Delivered"]];
  const GOODS = ["Transportation of Household Goods", "Transportation of Car", "Transportation of Office Goods"];

  const lrStatus = (l) => {
    if ((l.shipment_status || "") === "delivered") return { label: "Delivered", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/40" };
    if (!l.vehicle_number || !l.driver_name || !l.driver_phone) return { label: "Docs Missing", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/40" };
    return { label: "Active", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/40" };
  };

  return (
    <section className="border-2 border-primary p-4 bg-primary/5" data-testid="lorry-receipt-panel">
      <div className="flex items-center justify-between mb-1">
        <SectionTitle icon={FileText}>Lorry Receipts ({lrs.length})</SectionTitle>
        {!f && <Button size="sm" className="rounded-none gap-1.5 font-bold uppercase tracking-wide" onClick={openNew} data-testid="add-new-lr-btn"><Plus className="h-4 w-4" /> Add New LR</Button>}
      </div>
      <p className="text-xs text-muted-foreground mb-4">Create a separate LR per truck (e.g. one for HHG, one for Car). Each LR has its own vehicle, driver, tracking link & status.</p>

      {lrs.length > 0 && (() => {
        const idx = Math.min(activeTruck, lrs.length - 1);
        const cur = lrs[idx];
        return (
        <div className="mb-4">
          <div className="flex gap-1 overflow-x-auto border-b border-border mb-3" data-testid="truck-tabs">
            {lrs.map((l, i) => {
              const st = lrStatus(l);
              const active = i === idx;
              return (
                <button
                  key={l.id}
                  onClick={() => setActiveTruck(i)}
                  data-testid={`truck-tab-${i + 1}`}
                  className={`shrink-0 flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wide border-b-2 -mb-px transition-colors ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                >
                  <Truck className="h-3.5 w-3.5" /> Truck {i + 1}
                  <span className={`px-1.5 py-0.5 text-[9px] border ${st.cls}`}>{st.label}</span>
                </button>
              );
            })}
          </div>
          <div className="border border-border bg-card p-3" data-testid={`lr-card-${cur.suffix || cur.lr_number}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <span className="font-mono-num text-sm text-primary font-bold">{cur.lr_number}</span>
                <span className="text-xs text-muted-foreground ml-2">{cur.goods_type || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Select value={cur.shipment_status || "at_origin"} onValueChange={(s) => setStatus(cur, s)}>
                  <SelectTrigger className="rounded-none h-8 w-[140px] text-xs bg-background" data-testid={`lr-status-${cur.suffix}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{SHIP.map(([v, lb]) => <SelectItem key={v} value={v}>{lb}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1.5">Vehicle: <b className="text-foreground">{cur.vehicle_number || "—"}</b> · Driver: <b className="text-foreground">{cur.driver_name || "—"}</b> {cur.driver_phone ? `(${cur.driver_phone})` : ""} · Freight: <b className="text-foreground">₹{Number(cur.vendor_cost || 0).toLocaleString("en-IN")}</b></div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Vendor: <b className="text-foreground">{cur.vendor_name || cur.carrier_name || "—"}</b></div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs h-8" onClick={() => openEdit(cur)} data-testid={`edit-lr-${cur.suffix}`}><FileText className="h-3.5 w-3.5" /> Edit</Button>
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs h-8" onClick={() => download(cur)} data-testid={`download-lr-${cur.suffix}`}><Download className="h-3.5 w-3.5" /> LR PDF</Button>
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs h-8" onClick={() => downloadDoc(cur, "manifest")} data-testid={`manifest-lr-${cur.suffix}`}><ClipboardList className="h-3.5 w-3.5" /> Manifest</Button>
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs h-8" onClick={() => downloadDoc(cur, "challan")} data-testid={`challan-lr-${cur.suffix}`}><Truck className="h-3.5 w-3.5" /> Challan</Button>
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs h-8" onClick={() => downloadDoc(cur, "carrier-risk")} data-testid={`carrier-risk-lr-${cur.suffix}`}><FileText className="h-3.5 w-3.5" /> Risk Receipt</Button>
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs h-8 text-emerald-600 border-emerald-500/40" onClick={() => sendTracking(cur)} data-testid={`track-lr-${cur.suffix}`}><MapPin className="h-3.5 w-3.5" /> Tracking Link</Button>
              <Button size="sm" variant="outline" className="rounded-none gap-1 text-xs h-8 text-red-600 border-red-500/40" onClick={() => removeLr(cur)} data-testid={`delete-lr-${cur.suffix}`}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
            </div>
          </div>
        </div>
        );
      })()}

      {!f ? (
        lrs.length === 0 && <p className="text-sm text-muted-foreground">No LRs yet. Click <b>Add New LR</b> to create the first one.</p>
      ) : (
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold uppercase tracking-wide">{editingId ? "Edit LR" : "New LR"}</span>
            <Button size="sm" variant="ghost" className="rounded-none text-xs" onClick={() => { setF(null); setEditingId(null); }}>Cancel</Button>
          </div>
          <Group title="Header & Metadata">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Carrier / Vendor (from master)</Label>
              <Select value={f.vendor_id || "__manual__"} onValueChange={pickVendor}>
                <SelectTrigger className="rounded-none bg-card h-9" data-testid="lr-vendor-select"><SelectValue placeholder="Pick a vendor…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__manual__">Manual / Not in list</SelectItem>
                  {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.city ? ` · ${v.city}` : ""}{bidFor(v.id) != null ? ` · ₹${Number(bidFor(v.id)).toLocaleString("en-IN")}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Fld label="Carrier name (free text)" v={f.carrier_name} on={(x) => setTop("carrier_name", x)} span2 testid="lr-carrier-name" />
            {(lead.consignment_count || 1) > 1 && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Consignment (auto-fills winning vendor & freight)</Label>
                <Select value={String(f.consignment_idx ?? 0)} onValueChange={(v) => { const ci = Number(v); setF((p) => ({ ...p, consignment_idx: ci, vendor_id: "", vendor_name: "", vendor_cost: "" })); }}>
                  <SelectTrigger className="rounded-none bg-card h-9" data-testid="lr-consignment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(lead.consignments || Array.from({ length: lead.consignment_count || 1 })).map((c, i) => <SelectItem key={i} value={String(i)}>RFQ {i + 1}{i === 0 ? " · Full Order" : (c?.description ? ` · ${c.description}` : "")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Fld label="Execution date (auto: pickup − 1)" type="date" v={f.execution_date} on={(x) => setTop("execution_date", x)} testid="lr-exec-date" />
            <Fld label="Vehicle number" v={f.vehicle_number} on={(x) => setTop("vehicle_number", x)} testid="lr-vehicle" />
            <Fld label="Driver name" v={f.driver_name} on={(x) => setTop("driver_name", x)} testid="lr-driver" />
            <Fld label="Driver contact number" v={f.driver_phone} on={(x) => setTop("driver_phone", x)} testid="lr-driver-phone" />
            <Fld label="Freight / Vendor cost for this truck (₹)" type="number" v={f.vendor_cost} on={(x) => setTop("vendor_cost", x)} testid="lr-vendor-cost" />
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Bill To <span className="text-muted-foreground normal-case">(for records — not printed on LR)</span></Label>
              <Input className="rounded-none bg-card h-9" value={f.bill_to || ""} onChange={(e) => setTop("bill_to", e.target.value)} placeholder="Type a new company name…" data-testid="lr-bill-to" />
              {companies.length > 0 && (
                <Select value="" onValueChange={(v) => setTop("bill_to", v)}>
                  <SelectTrigger className="rounded-none bg-card h-9" data-testid="lr-bill-to-existing"><SelectValue placeholder="…or pick an existing company" /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </Group>
          <Group title="Party Profiles">
            <Fld label="Consignor name" v={f.consignor.name} on={(x) => setNested("consignor", "name", x)} />
            <Fld label="Consignor GSTIN" v={f.consignor.gstin} on={(x) => setNested("consignor", "gstin", x)} error={gstErr(f.consignor.gstin)} />
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Consignor address</Label>
              <PlacesAutocomplete className="bg-card h-9" value={f.consignor.address ?? ""} onChange={(x) => setNested("consignor", "address", x)} onSelect={(p) => setNested("consignor", "address", p.formatted_address)} />
            </div>
            <Fld label="Consignee name" v={f.consignee.name} on={(x) => setNested("consignee", "name", x)} testid="lr-consignee" />
            <Fld label="Consignee GSTIN" v={f.consignee.gstin} on={(x) => setNested("consignee", "gstin", x)} error={gstErr(f.consignee.gstin)} />
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Consignee address</Label>
              <PlacesAutocomplete className="bg-card h-9" value={f.consignee.address ?? ""} onChange={(x) => setNested("consignee", "address", x)} onSelect={(p) => setNested("consignee", "address", p.formatted_address)} />
            </div>
          </Group>
          <Group title="Description of Goods">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-xs">Type of transportation</Label>
              <Select value={f.goods_type} onValueChange={(v) => setTop("goods_type", v)}>
                <SelectTrigger className="rounded-none bg-card" data-testid="lr-goods-type"><SelectValue /></SelectTrigger>
                <SelectContent>{GOODS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              {(customOpen || f.custom_description) ? (
                <div className="space-y-1.5">
                  <Label className="text-xs">Custom description</Label>
                  <Textarea rows={2} className="rounded-none bg-card text-sm" value={f.custom_description} onChange={(e) => setTop("custom_description", e.target.value)} data-testid="lr-custom-description" placeholder="Manual description of goods…" />
                </div>
              ) : (
                <Button type="button" variant="outline" size="sm" className="rounded-none gap-1 text-xs" onClick={() => setCustomOpen(true)} data-testid="lr-add-custom-desc"><Plus className="h-3.5 w-3.5" /> Add Custom Description</Button>
              )}
            </div>
          </Group>
          <Group title="Freight Challan (editable — prints on the Goods Dispatch Memo)">
            <Fld label="Total Freight (₹)" type="number" v={f.financial?.basic_freight} on={(x) => setNested("financial", "basic_freight", x)} testid="lr-chl-freight" />
            <Fld label="Office Mamul (-) (₹)" type="number" v={f.financial?.office_mamul} on={(x) => setNested("financial", "office_mamul", x)} testid="lr-chl-mamul" />
            <Fld label="Detention / Unloading (₹)" type="number" v={f.financial?.detention_unloading} on={(x) => setNested("financial", "detention_unloading", x)} testid="lr-chl-detention" />
            <Fld label="Advance (₹)" type="number" v={f.financial?.advance_paid} on={(x) => setNested("financial", "advance_paid", x)} testid="lr-chl-advance" />
            <Fld label="Balance (₹) — blank to auto-calc" type="number" v={f.financial?.balance} on={(x) => setNested("financial", "balance", x)} testid="lr-chl-balance" />
          </Group>
          <Group title="Documentation">
            <Fld label="Invoice No. of Goods" v={f.invoice_no} on={(x) => setTop("invoice_no", x)} testid="lr-invoice-no" />
            <Fld label="HSN Code" v={f.hsn_code} on={(x) => setTop("hsn_code", x)} testid="lr-hsn" />
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Remarks (printed on LR PDF)</Label>
              <Textarea rows={2} className="rounded-none bg-card text-sm" value={f.remarks} onChange={(e) => setTop("remarks", e.target.value)} data-testid="lr-remarks" placeholder="Any remarks for this LR…" />
            </div>
          </Group>
          <Group title="Insurance Declaration">
            <div className="sm:col-span-2 space-y-2 text-sm">
              <div className="text-xs text-muted-foreground">I the consignor have stated that:</div>
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="ins-decl" checked={f.insurance_declared === "not_insured"} onChange={() => setTop("insurance_declared", "not_insured")} className="accent-primary" data-testid="lr-ins-not" /> he has <b>not</b> insured the consignment</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="ins-decl" checked={f.insurance_declared === "insured"} onChange={() => setTop("insurance_declared", "insured")} className="accent-primary" data-testid="lr-ins-yes" /> he has insured the consignment</label>
            </div>
            {f.insurance_declared === "insured" && <>
              <Fld label="Company" v={f.insurance_company} on={(x) => setTop("insurance_company", x)} testid="lr-ins-company" />
              <Fld label="Policy No" v={f.insurance_policy_no} on={(x) => setTop("insurance_policy_no", x)} testid="lr-ins-policy" />
              <Fld label="Date" type="date" v={f.insurance_date} on={(x) => setTop("insurance_date", x)} testid="lr-ins-date" />
              <Fld label="Amount" v={f.insurance_amount} on={(x) => setTop("insurance_amount", x)} testid="lr-ins-amount" />
              <Fld label="Risk" v={f.insurance_risk} on={(x) => setTop("insurance_risk", x)} testid="lr-ins-risk" />
            </>}
          </Group>
          <p className="text-[11px] text-muted-foreground border border-border bg-muted/50 p-2 mb-4">Legal notices, <b>Ahmedabad jurisdiction</b>, owner's-risk terms and signature lines are printed automatically on the Lorry Receipt PDF.</p>
          <Button className="rounded-none font-bold uppercase tracking-wide gap-2" onClick={save} data-testid="save-lr-btn"><FileText className="h-4 w-4" /> {editingId ? "Update LR" : "Save New LR"}</Button>
        </div>
      )}
    </section>
  );
}

const Group = ({ title, children }) => (  <div className="mb-4">
    <div className="text-[11px] font-bold uppercase tracking-widest text-primary mb-2">{title}</div>
    <div className="grid sm:grid-cols-2 gap-3">{children}</div>
  </div>
);
const Fld = ({ label, v, on, type = "text", span2 = false, testid, error }) => (
  <div className={`space-y-1.5 ${span2 ? "sm:col-span-2" : ""}`}>
    <Label className="text-xs">{label}</Label>
    <Input type={type} className={`rounded-none bg-card h-9 ${error ? "border-destructive focus-visible:ring-destructive" : ""}`} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testid} />
    {error && <p className="text-[11px] text-destructive" data-testid={testid ? `${testid}-error` : undefined}>{error}</p>}
  </div>
);
const LegalFld = ({ label, v, on }) => (
  <div className="space-y-1.5 sm:col-span-2">
    <Label className="text-xs">{label}</Label>
    <Textarea rows={2} className="rounded-none bg-card text-xs" value={v ?? ""} onChange={(e) => on(e.target.value)} />
  </div>
);

function AuditPanel({ audit }) {
  return (
    <section>
      <SectionTitle icon={History}>Audit Trail</SectionTitle>
      <div className="border-l-2 border-border ml-1 pl-4 space-y-4">
        {(audit || []).map((a) => (
          <div key={a.id} className="relative" data-testid="audit-entry">
            <div className="absolute -left-[21px] top-1 h-2 w-2 bg-primary rounded-full" />
            <div className="text-sm font-medium">{a.action}</div>
            {a.details && <div className="text-xs text-muted-foreground">{a.details}</div>}
            <div className="text-[11px] text-muted-foreground mt-0.5">{a.changed_by_name} · {new Date(a.timestamp).toLocaleString()}</div>
          </div>
        ))}
        {(!audit || !audit.length) && <div className="text-sm text-muted-foreground">No history yet.</div>}
      </div>
    </section>
  );
}

const Info = ({ label, value }) => (<div><div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div><div className="text-sm font-medium mt-0.5">{value}</div></div>);
const SectionTitle = ({ icon: Icon, children }) => (<h3 className="font-heading font-bold uppercase tracking-tight text-lg mb-3 flex items-center gap-2"><Icon className="h-4 w-4 text-primary" />{children}</h3>);
const Row = ({ label, val }) => (<div className="flex justify-between p-3 border-b border-border"><span className="text-muted-foreground">{label}</span><span>{val}</span></div>);
